import { copyText } from './copied'
import { otherJobsChoice } from './offer-agreement-choice'
import type { BookingOtherJobs } from '../functions/lib/customer-offer-data'
import { openBookingWelcome } from './booking-welcome'
import { regularChoiceOn } from './enquiry-regular-choice'
import type { ExtraOffer, OfferChoice } from './offer-extras'
import { addressLine } from './address-words'
import { timeWheel } from './time-wheel'
import { spotButton } from './exact-spot'
import { howOftenPicker, type OftenKind } from './how-often'
import { extrasBox, extrasOf, extrasTotal, type Extra } from './extras'
import { askExtras, doneExtras } from './done-extras'
import { currencySymbol, currencyWord, postcodeWords, roundConfig } from './round-config'
import { findCustomers } from './customer-find'
import { acceptedVisitMessage, agreedTimeKey, comingUpDays, timeIsAgreed } from './rebook-message'
import { messageThemOffer } from './book-message'
import { sendControl } from './contact-ui'
import { linkFor, wayFor, wayWords } from './contact'
import { offerUndo, undoPatch, type UndoLink } from './undo'
import { invoiceAfterJobLink, invoicesHere } from './invoices'
import { kindLabel } from './trade'
import { priceFor } from './pricing'
import { api, dismiss as dismissQueued, getQueue, mutate, offQueueChange, onQueueChange, queuePartition, readCache } from './api'
import { getMe, isWorker, pricesHidden, worksAlone } from './me'
import { flagMissingPayment, paymentPills } from './payment-pills'
import { doorWords, moneyPending, toCollect } from './brought'
import { attachPhotos } from './photo-note'
import { attachVoiceNote } from './voice-note'
import { buzz } from './haptic'
import { reducedMotion } from './motion'
import { diaryLandOn } from './diary-land'
import type { Clean, Customer, Lead, RoundDay, SchedulePayload } from './types'
import { addDaysISO, clear, cycleLabel, cycleOptions, dayLabel, el, mondayOf, money, nextBookedMessage, parseMoney, visitSavedMessage, shortDate, toast, todayISO, shownVisitTime } from './ui'
import { addressWithoutPostcode, cycleDaysFromWeeks, postcodeAtEnd } from './lead-conversion'
import { bookedTick } from './book-icons'
import { diaryCustomer, standingWords, visitWords } from './visit-words'
import { setTimesOn } from './business-settings'
import { parkingClash, parkingWarning, type ParkingCustomer } from './parking-ask'
import { partLabel, partView, type InsidePart, type OutsidePart, type PartView } from './part-plan'
import { isContact, isSuggestion, nextPlanningDate, overlapNames, workload } from './planning'
import { bookWords, chipDates, dayLineWords, dayLoad, DURATION_PRESETS, minutesWords, otherThanOwner, visitsWords, workerChoices, type DayLoad } from './booking-days'
import { firstName } from './late-message'
import { GONE_DAY_WORDS } from './screens/month'
import { severalJobsOn } from './job-names'
import { diaryDayOrder } from './diary-stops'
import { keepPendingJob, pendingJobFor } from './pending-jobs'
import { everyWords, extraJobOf, jobOneOf, lastDoneOf, nextAfterDone, visitJobId, visitsOf, windowsDayBefore, type CustomerJob, type Job, type JobKind } from './jobs'
import { cursorOnOpen, keepAboveKeyboard } from './sheet-keyboard'
import { hasInsides, outsidePence, priceWords } from './inside-price'

// A quote's saved details (0092): the insides and first-visit extras Book them in starts on.
function quotedOf(json: string | null | undefined): { insides: { pence: number; every: boolean } | null; extras: Extra[] } | null {
  if (!json) return null
  try {
    const o = JSON.parse(json) as { insides?: { pence?: number; every?: boolean } | null; extras?: Extra[] }
    const insides = o.insides && Number.isInteger(o.insides.pence) && (o.insides.pence as number) > 0 ? { pence: o.insides.pence as number, every: o.insides.every === true } : null
    return { insides, extras: Array.isArray(o.extras) ? o.extras.filter((e) => e && typeof e.name === 'string' && Number.isInteger(e.count)) : [] }
  } catch { return null }
}

// The fields the log-completed panel needs from a visit. Satisfied both by
// the full API rows and by the narrow /api/today rows, so the worker's Done
// button can open this SAME panel with data the Today screen already holds —
// a worker may not read /api/customers or /api/schedule, so the panel must
// not go and fetch them.
export type DoneClean = Pick<
  Clean,
  'id' | 'version' | 'date' | 'time_slot' | 'amount_pence' | 'note' | 'state' | 'payment_status' | 'payment_method' | 'cash_collected_pence'
> & Partial<Pick<Clean, 'inside_pence' | 'extras_json' | 'part' | 'rest_of' | 'confirmed' | 'time_agreed_for' | 'from_credit_pence' | 'paid_in_pence' | 'cash_taken_pence' | 'cash_unconfirmed' | 'money_pending'>> & { account_owed_pence?: number }

interface BookingOptions {
  customerOffer?: { id: number; version: number }
  offersDeferred?: boolean
  acceptedOffer?: { choice: OfferChoice; offer: ExtraOffer }
  customerId?: number
  cleanId?: number
  date?: string
  agreedDate?: string // Explicit agreed day; changing it resets agreement.
  // Accepted-day reply: retain the visit request and review a message only after a saved ACK.
  replyConfirmation?: boolean
  initialVisitNote?: string
  onBooked?: () => void
  completed?: boolean
  // Diary receipt: only a real acknowledged completion, never pending/error/noop.
  onCompleted?: () => void
  // Worker Done: the customer + visit from the Today screen, pre-read. When
  // present (and the signed-in role is worker) the panel renders the reduced
  // form and touches the network only for the save itself.
  // inside_price_pence: their usual insides price, the Insides tile's starting figure on Job done (owner's Today only).
  snapshot?: { customer: { name: string; address: string | null; postcode: string | null; inside_price_pence?: number | null }; clean: DoneClean }
  // Opened straight after Slide to finish: offers "Not finished yet", which
  // closes the panel and takes the finish time back off (review 2026-09-24:
  // the Undo for the slide sat behind this panel for its whole ten seconds).
  notFinished?: () => void
  // A finished job's Note (Today, 13 Oct 2026, Liam: "There is no way to leave a note for example John's was especially
  // dirty this time"): the same sheet, opened on the note box.
  openNote?: boolean
  // The note saved there, handed straight back so the job's card shows it at once (Liam, 13 Oct 2026: "The message we
  // add should instanly appear so they know its added"), before the day is read again.
  onNoteSaved?: (note: string | null, version: number | null) => void
  // The welcome's "Book your first visit" (Liam, 29 Sep): each part to fill in glows in turn, with a one-line
  // hint and Next, and after saving the diary shows the new visit, glowing, with where to find it on the day.
  guide?: boolean
  // Several jobs (step 2): which job to book. null = job 1 (the customer's main job); left out = job 1 too. Only
  // read on a round with several jobs on.
  jobId?: number | null
  // Book them in (Liam, 19 Oct 2026, picked A): an enquiry booked straight from its card (Messages, New jobs). The same
  // sheet for someone not yet a customer: nothing is saved until Book, then the enquiry, the customer made from it and
  // the visit go in one save (POST /api/leads/:id/book, functions/lib/book-lead.ts), with the first clean, the price
  // after it, how often and the postcode typed here.
  enquiry?: Lead
  // The day and time picked here are the ones arranged with them (Book them in, and "Yes, this is them" before it):
  // picking a day turns Day agreed on and setting a time turns Time agreed on (reviewers, 19 Oct 2026: never for a day
  // only suggested). Changing them still turns them off, as everywhere else.
  arranged?: boolean
  // Book them in's "Already a customer? Pick them": back to the choice of customer (src/book-enquiry.ts), handed what
  // was picked and typed so far, so the next sheet starts from it (Sonnet, 19 Oct 2026).
  pickCustomer?: (draft: BookDraft) => void
  // What an earlier Book them in sheet had picked and typed, put back as if picked again.
  draft?: BookDraft
  // "Yes, this is them" (Book them in): this customer's own sheet, and once its visit is booked the enquiry is won and
  // put on their page. Nothing is saved before Book, here either (Sol, 19 Oct 2026).
  linkLead?: Lead
  addOfferToVisit?: boolean
}

// extras: the extras added, kept through "Already a customer?" (0087; Sonnet's extras review 5).
export interface BookDraft { otherJobs?:BookingOtherJobs; date?: string; time?: string; first?: string; regular?: string; every?: string; postcode?: string; worker?: string; duration?: string; note?: string; extras?: Extra[] }

// The enquiry as the sheet's customer until it is one: its name, address and postcode, its quote and how often.
function enquiryCustomer(lead: Lead): Customer {
  const choice = regularChoiceOn(lead)
  const postcode = postcodeAtEnd(lead.address, roundConfig().country)
  const extra = lead as Lead & { cycle_text?: string | null; quote_json?: string | null }
  const quotedText = extra.cycle_text?.trim() ? extra.cycle_text.trim() : null
  const quoted = quotedOf(extra.quote_json)
  let quotedParking: string | null = null
  try { const o = extra.quote_json ? JSON.parse(extra.quote_json) as { parking?: unknown } : {}; quotedParking = typeof o.parking === 'string' && o.parking.trim() ? o.parking.trim() : null } catch { quotedParking = null }
  return {
    id: 0, name: lead.name, address: addressWithoutPostcode(lead.address, postcode), postcode, phone: lead.phone, email: lead.email,
    // A Just once or Ad hoc quote (0092) opens on its own tile, never the round's usual weeks (the Help terminal's check).
    status: 'active', cycle_days: choice ? choice.days : quotedText ? null : cycleDaysFromWeeks(lead.cycle_weeks) ?? getMe()?.defaultCycleDays ?? null, cycle_text: choice ? choice.days ? null : 'Ad-hoc' : quotedText,
    reg_price_pence: choice?.pence ?? lead.regular_pence, first_clean_price_pence: choice?.first ?? lead.initial_pence, inside_price_pence: quoted?.insides?.pence ?? null,
    notes: null, job_notes: null, parking_restrictions: quotedParking, preferred_times: null, version: 0,
  } as unknown as Customer
}

// The saved visit is the authority for what was added; a retry carries the same receipt.
function quoteOfferReceipt(visit: Clean): HTMLElement[] {
  const meta = (visit as Clean & { _quote_offer?: { included: string[]; added: { name: string; pence: number }[] } })._quote_offer
  const extra = extrasOf(visit.extras_json)
  if (!meta && (!(visit as Clean & { breakdown_json?: string | null }).breakdown_json || !extra.length)) return []
  const rows: HTMLElement[] = []
  if (meta?.included.length) rows.push(el('p', { class: 'booking-quote-included' }, `Already included: ${meta.included.join(', ')}. No extra charge.`))
  if (meta?.added.length) rows.push(el('p', { class: 'booking-quote-added' }, `Added: ${meta.added.map(e => `${e.name} ${money(e.pence)}`).join(', ')}.`))
  else if (meta) rows.push(el('p', { class: 'booking-quote-added' }, 'No extra charge added.'))
  else rows.push(el('p', { class: 'booking-quote-added' }, `Other work on this visit: ${extra.map(e => `${e.name} ${money(e.count * (e.each_pence ?? 0))}`).join(', ')}.`))
  rows.push(el('p', { class: 'booking-quote-total' }, `Total for this visit: ${money(visit.amount_pence ?? 0)}`))
  return rows
}

// Booked visits on or before today that nobody has marked done or cancelled (not contact reminders), oldest first:
// the work Log completed work can finish.
function openVisits(cleans: Clean[]): Clean[] {
  const today = todayISO()
  return cleans.filter((c) => c.state === 'booked' && c.date <= today && !isContact(c)).sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
}

// The several-jobs half of the editor (PLAN 3): the customer's jobs, which one this visit is for, and a new one.
interface JobPick {
  jobs: Job[]
  extra: CustomerJob[]
}

const NEW_JOB_KINDS: [JobKind, string][] = [['gutter', 'Gutters'], ['window', 'Windows'], ['other', 'Something else']]
let openDialog: HTMLDialogElement | null = null
// Book a visit beside the week on a computer (design A, Liam, 3 Nov 2026: "Yeah thats fair lets go recommended";
// https://claude.ai/artifact/5dtvSTLK1nNgzQphZFw2Ne): on the Diary, at 1100 wide or more, the panel sits on the right
// with no dark cover, the week stays usable beside it, a click on a day in the week picks that day, and the picked day
// lights in the week. Narrower screens, phones and every other screen keep the panel over a darkened page.
let sidePick: ((iso: string) => void) | null = null
let sideBound = false
const sideWanted = (): boolean => typeof matchMedia === 'function' && matchMedia('(min-width: 1100px)').matches && /^#\/(schedule|diary)/.test(location.hash || '#/schedule')
function markSideDay(iso: string): void {
  for (const d of document.querySelectorAll<HTMLElement>('.booking-side-day')) d.classList.remove('booking-side-day')
  if (!iso) return
  for (const d of document.querySelectorAll<HTMLElement>(`#screen .day-card[data-date="${iso}"], #screen .diary-desk-strip [data-date="${iso}"]`)) d.classList.add('booking-side-day')
}
function bindSide(): void {
  if (sideBound) return
  sideBound = true
  // A click on a day in the week, while the panel is beside it, picks that day instead of opening it.
  document.addEventListener('click', (e) => {
    if (!sidePick || !document.body.classList.contains('booking-side-open')) return
    const t = e.target as Element | null
    if (!t || t.closest('dialog.booking-dialog')) return
    const day = t.closest<HTMLElement>('#screen .day-card[data-date], #screen .diary-desk-strip [data-date]')
    if (!day || t.closest('a, .row-menu, .day-row, .tray-row, .diary-row') && !t.closest('.day-head')) return
    const iso = day.dataset.date ?? ''
    if (!iso || iso < todayISO()) return
    e.preventDefault(); e.stopPropagation()
    sidePick(iso)
  }, true)
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || e.defaultPrevented || !document.body.classList.contains('booking-side-open')) return
    const open = document.querySelector<HTMLDialogElement>('dialog.booking-dialog.booking-side[open]')
    if (!open || document.querySelector('dialog[open]:not(.booking-side)')) return
    e.preventDefault()
    open.querySelector<HTMLButtonElement>('.booking-head button')?.click()
  })
}

// One editor lives outside the routed screen: refreshes cannot erase its draft.
export function openBooking(options: BookingOptions = {}): void {
  if (openDialog) { openDialog.focus(); return }
  if (options.enquiry) options.arranged = true
  const identity = queuePartition()
  const sameIdentity = (): boolean => identity !== null && identity === queuePartition()
  const origin = document.activeElement as HTMLElement | null
  const dialog = el('dialog', { class: 'booking-dialog', 'aria-labelledby': 'booking-title' })
  openDialog = dialog
  // The panel slides away rather than vanishing (2026-09-24): every close()
  // on it first plays the slide (styles.css .sheet-closing), then really
  // closes. Escape and the browser's own close go straight through.
  const closeNow = dialog.close.bind(dialog)
  dialog.close = (returnValue?: string): void => {
    if (!dialog.open || dialog.classList.contains('sheet-closing')) return
    const still = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    if (still || typeof dialog.animate !== 'function') {
      closeNow(returnValue)
      return
    }
    dialog.classList.add('sheet-closing')
    // The next Done on the day can open its own panel while this one slides.
    if (openDialog === dialog) openDialog = null
    let shut = false
    const finish = (): void => {
      if (shut) return
      shut = true
      closeNow(returnValue)
    }
    // The panel's own slide, not an animation inside it (a ticked button).
    dialog.addEventListener('animationend', (e) => {
      if (e.target === dialog) finish()
    })
    window.setTimeout(finish, 320) // no animation (or it never ends): close anyway
  }
  const heading = el('h2', { id: 'booking-title' }, options.completed ? 'Log completed work' : 'Book a visit')
  const close = el('button', { type: 'button', 'aria-label': 'Close booking editor' }, 'Close')
  const content = el('div', { class: 'booking-content' })
  dialog.append(el('header', { class: 'booking-head' }, heading, close), content)
  let busy = false
  let closed = false
  type BookingQuote = { id: number; number: number; total_pence: number }
  let bookingQuote: BookingQuote | null = null
  let customerQuote: BookingQuote | null = null
  const quoteLabel = (q: BookingQuote): string => `Q-${String(q.number).padStart(4, '0')}`
  async function readBookingQuote(lead: Lead): Promise<void> {
    let whole = false
    try { whole = JSON.parse(lead.quote_json ?? '{}').whole_breakdown === true } catch { /* ordinary quote */ }
    if (!whole) return
    const data = await api<{ rows: (BookingQuote & { sent_at: string | null })[] }>(`/api/leads/${lead.id}/quotes`)
    bookingQuote = data.rows.find(q => q.sent_at !== null) ?? null
  }
  // Log completed work opened with no visit chosen (the customer page): the visit booked today that it finishes,
  // when there is one (bug hunt 8 Oct 2026: it used to add a second visit beside it).
  let finishing: number | null = null
  // "Save visit & review message" (an accepted reply): its Undo is offered once the review screen is closed, so the
  // bar is not hidden behind it (bug hunt 8 Oct 2026, messages 11). Cleared when the visit is opened again to edit.
  let undoOnClose: (() => void) | null = null
  // A sheet with words typed and not saved saves them on the way out instead (the job's Note: Liam, 22 Oct 2026, "i
  // accidently clicked close button when i typed a message out i wonder if close should also save if note has changed").
  // Answers true when it is saving (the save closes the sheet itself).
  let saveOnClose: (() => boolean) | null = null
  const dismiss = (): void => { if (!busy && !saveOnClose?.()) dialog.close() }
  close.addEventListener('click', dismiss)
  dialog.addEventListener('cancel', e => { if (busy || saveOnClose?.()) e.preventDefault() })
  dialog.addEventListener('close', () => {
    closed = true
    if (dialog.classList.contains('booking-side')) { document.body.classList.remove('booking-side-open'); sidePick = null; markSideDay('') }
    freeKeyboard()
    dialog.remove()
    const offer = undoOnClose
    undoOnClose = null
    offer?.()
    // Only this panel's own claim: a newer one may already be open.
    if (openDialog === dialog) openDialog = null
    if (origin?.isConnected) origin.focus()
  })
  document.body.append(dialog)
  const side = sideWanted() && !options.completed
  if (side) {
    bindSide()
    dialog.classList.add('booking-side')
    document.body.classList.add('booking-side-open')
    dialog.show()
    requestAnimationFrame(() => dialog.querySelector<HTMLElement>('input, select, textarea, button:not(.booking-head button)')?.focus({ preventScroll: true }))
  } else dialog.showModal()
  // On a phone the panel stands on the keyboard while a box in it is typed in (sheet-keyboard.ts).
  const freeKeyboard = keepAboveKeyboard(dialog)

  // The first-booking guide: one hint bar at the top of the panel and one part glowing at a time.
  let guideHint: HTMLElement | null = null
  const guideTo = (text: string, target: HTMLElement | null, next?: () => void, focus = false): void => {
    if (!options.guide) return
    dialog.querySelectorAll('.guide-glow').forEach((n) => n.classList.remove('guide-glow'))
    if (!guideHint) {
      guideHint = el('div', { class: 'guide-hint', role: 'status', 'aria-live': 'polite' })
      dialog.querySelector('.booking-head')?.after(guideHint)
    }
    const words = el('span', {}, text)
    guideHint.replaceChildren(words)
    if (next) {
      const go = el('button', { type: 'button', class: 'link-button guide-next' }, 'Next')
      go.addEventListener('click', next)
      guideHint.append(go)
    }
    if (target) {
      target.classList.add('guide-glow')
      target.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
      // Moving on with Next takes the keyboard to the part now glowing (the Next button itself is replaced).
      if (focus) (target.matches('input, select, button, textarea') ? target : target.querySelector<HTMLElement>('input, select, button, textarea'))?.focus()
    }
  }

  // The worker's Done panel: same dialog, same save machinery, but only the
  // two things a worker may change (how it was paid + a note) — the fields
  // the server would refuse (date, price, worker, kind) are not offered at
  // all, and there is no day-preview because that read is owner-only.
  function renderWorkerDone(snap: NonNullable<BookingOptions['snapshot']>): void {
    const clean = snap.clean
    // Opened on a job already logged, it is the Change payment panel (worker
    // review 2026-09-24): the same buttons, opening on the saved answer.
    let changing = clean.state === 'done'
    // Job done (Liam, 12 Oct 2026: "log completed work needs redesigning its not on brand and improve the
    // animations"): the job just slid to finished leads with a green tick that pops, then how it was paid, then
    // the round Note, Photo and Voice note buttons the started card has, and one lemon Save. Each part rises in
    // turn as the sheet arrives; reduced motion shows it all at once (styles.css "Job done").
    heading.textContent = changing ? (options.openNote ? 'Note for this job' : 'Change payment') : 'Job done'
    dialog.classList.add('worker-done')
    if (!changing) dialog.classList.add('worker-done-new')
    clear(content)
    const visitWords = (): string => `${dayLabel(clean.date)}${setTimesOn() ? ` · ${shownVisitTime(clean) ?? 'no set time'}` : ''}${pricesHidden() ? '' : ` · ${priceWords(clean) ?? 'price not recorded'}`}`
    const visitSummary = el('p', { class: 'muted' }, visitWords())
    content.append(el('section', { class: 'wd-summary wd-rise' },
      el('span', { class: 'wd-tick ic ic-check', 'aria-hidden': 'true' }),
      el('div', { class: 'wd-who' },
        el('h3', {}, snap.customer.name),
        el('p', { class: 'muted' }, addressLine(snap.customer.address, snap.customer.postcode, ' · ')),
        visitSummary)))
    const form = el('form', { class: 'booking-form' })
    const fields = el('fieldset', { class: 'booking-fields' })
    const field = (label: string, node: HTMLElement): HTMLElement => el('label', { class: 'booking-field' }, el('span', {}, label), node)
    // Was it paid? No / Yes, then Cash, Bank or Other (src/payment-pills.ts). A visit already done opens on its
    // recorded answer; a new one on No (Liam, 13 Oct 2026).
    // A worker not shown prices is asked how much cash they took (0021).
    const askCash = pricesHidden()
    // Credit on this visit (0043, Liam Q1 and Q2): "£24 · £5 from credit · £19 to collect", and Cash asks what was
    // taken, starting on the £19. More is kept as credit; less leaves the rest to pay.
    // Also what they owe from Squeegee: "£22.00 for this clean + £12.00 from Squeegee = £34.00", Cash starting on £34.
    // On a round with brought money every Cash asks what was taken (Astra r2, finding 12), starting on what is due.
    const door = askCash ? null : doorWords(clean, money)
    // A visit already done (Change payment) asks too, starting on the cash on file (Astra r3, finding 10).
    const askTaken = !askCash && (door !== null || getMe()?.broughtMoney === true)
    const cashOnFile = clean.state === 'done' && clean.payment_status === 'paid' && clean.payment_method === 'cash' && (clean.cash_taken_pence ?? 0) > 0 ? clean.cash_taken_pence! : null
    const startTaken = cashOnFile ?? (door ? door.takePence : toCollect(clean))
    // "Was it paid?" No / Yes, then Cash, Bank or Other (Liam, 13 Oct 2026; src/payment-pills.ts paidFirstPills).
    const payment = paymentPills(clean.state === 'done' ? (clean.payment_status === 'paid' ? clean.payment_method ?? 'paid' : clean.payment_status) : null, { askCash: askCash || askTaken, cashPence: askTaken ? startTaken : clean.cash_collected_pence ?? null, paidFirst: true })
    const note = el('textarea', { rows: '3', maxlength: '2000', placeholder: 'e.g. Side gate locked, did the front only' })
    note.value = clean.note ?? ''
    // Part done (Liam, 13 Oct 2026, at Rani's; functions/lib/part-done.ts): a quiet "Part done?" under the job, nothing
    // more on an ordinary day. Also on Change payment for a whole job done (Liam, 13 Oct 2026: "No way to go back to Rani
    // and change price and select fronts only"), never on a part already or on the rest of one. Tapped (22 Oct 2026, "I'm
    // not sure when I click part done if I can just do downs or ups"; src/part-plan.ts): a job with no insides has Fronts,
    // Backs, Downstairs, Upstairs or Other; a job with insides has Outsides and Insides, each All, a half or None. Today's
    // charge is filled in from them (the owner can change it; a worker never sets a price), and what is left is booked as
    // its own visit for a day this week. Other is a few words of their own, as before.
    let part: 'split' | null = null
    let otherOpen = false
    const whole = clean.amount_pence ?? null
    const insideNow = clean.inside_pence && clean.inside_pence > 0 ? clean.inside_pence : 0
    const hasInsides = insideNow > 0
    let outsidePart: OutsidePart = 'fronts'
    let insidePart: InsidePart = hasInsides ? 'all' : 'none'
    // Other's charge, as before: the insides and half the outsides.
    const halfPrice = whole === null ? null : insideNow + Math.round((whole - insideNow) / 2)
    const view = (): PartView => partView(whole, insideNow, outsidePart, insidePart)
    const defaultCharge = (): number | null => (otherOpen ? halfPrice : view().today)
    const ownerPrices = !isWorker() && !pricesHidden()
    const partBox = el('div', { class: 'wd-part' })
    const partOther = el('input', { type: 'text', maxlength: '40', class: 'wd-part-other', placeholder: 'e.g. front and sides', 'aria-label': 'What did you do?' }) as HTMLInputElement
    const partPrice = el('input', { type: 'text', inputmode: 'decimal', class: 'wd-part-price', 'aria-label': `Charge for today, in ${currencyWord()}` }) as HTMLInputElement
    const fillCharge = (): void => { const c = defaultCharge(); partPrice.value = c === null ? '' : (c / 100).toFixed(2) }
    const restWords = (): string => {
      if (!otherOpen && view().whole) return "That's the whole job: tap Whole job."
      if (!otherOpen && view().nothing) return "Nothing done: use Couldn't do it instead."
      let today: number | null = defaultCharge()
      if (ownerPrices) { try { today = parseMoney(partPrice.value) } catch { today = null } }
      const rest = whole !== null && today !== null && today <= whole ? whole - today : null
      const what = otherOpen ? 'The rest' : partLabel(view().restPart)
      return `${what}${rest !== null && !pricesHidden() ? `, ${money(rest)}` : ''}: booked for this week, no day yet.`
    }
    const restLine = el('p', { class: 'muted wd-part-rest', 'aria-live': 'polite' })
    // On a round that asks what cash was taken, the cash box starts on what is due today: less by what Part done takes
    // off (Codex), as long as nothing has been typed in it. Never over cash already recorded (GPT-6.1: Change payment
    // turned £60 taken into £30 taken).
    const followCash = (): void => {
      if (!askTaken || whole === null || startTaken === null || cashOnFile !== null) return
      let today: number | null = part === null ? whole : defaultCharge()
      if (part !== null && ownerPrices) { try { today = parseMoney(partPrice.value) } catch { today = null } }
      if (today === null || today > whole) return
      payment.suggestCash?.(startTaken - (whole - today))
    }
    const partAllowed = clean.rest_of == null && (!changing || !clean.part)
    // Extras? to the right of Part done? (src/done-extras.ts; Liam, 27 Oct 2026): the owner's first Done only, so Change
    // payment never adds them twice and a helper's extras wait for the owner (the drawings' next step). Part done can't
    // go with extras yet (functions/api/cleans/[id].ts part_with_extras), so each says so while the other is in use.
    const extras = ownerPrices && !changing && !isWorker() && !options.openNote
      ? doneExtras(clean, snap.customer.inside_price_pence ?? null, () => { drawPartLock(); repaintSave() }) : null
    // A helper's own (0091): the same tiles, no prices; what they tick waits for the owner's price (Worker notes).
    const asking = !extras && isWorker() && !changing && !options.openNote ? askExtras(clean, () => drawPartLock()) : null
    const extrasLink = extras?.link ?? asking?.link ?? null
    const extrasBooked = extrasOf(clean.extras_json).length > 0
    const partLockWords = (): string | null => (extrasBooked || (extras?.count() ?? 0) > (hasInsides ? 1 : 0) || (extras !== null && extras.changed()) || (asking?.count() ?? 0) > 0
      ? "Part done can't go with extras yet. Take the extras off to use Part done." : null)
    let partOpenEl: HTMLButtonElement | null = null
    const partLockLine = el('p', { class: 'muted wd-part-lock' })
    const drawPartLock = (): void => {
      const why = partLockWords()
      if (partOpenEl) partOpenEl.disabled = why !== null
      partLockLine.textContent = why ?? ''
      partLockLine.hidden = why === null
    }
    let repaintSave = (): void => undefined
    const chipRow = (label: string, choices: [string, string][], isOn: (v: string) => boolean, pick: (v: string) => void): HTMLElement => {
      const row = el('div', { class: 'pay-pills wd-part-chips wd-part-chips-wrap', role: 'group', 'aria-label': label })
      for (const [v, words] of choices) {
        const on = isOn(v)
        const b = el('button', { type: 'button', class: `pay-pill wd-part-chip${on ? ' pay-on' : ''}`, 'aria-pressed': String(on) }, words)
        b.addEventListener('click', () => { pick(v); fillCharge(); drawPart() })
        row.append(b)
      }
      return row
    }
    const drawPart = (): void => {
      clear(partBox)
      if (!partAllowed) return
      if (part === null) {
        followCash()
        const open = el('button', { type: 'button', class: 'link-button wd-part-open' }, 'Part done?') as HTMLButtonElement
        open.addEventListener('click', () => { part = 'split'; otherOpen = false; outsidePart = 'fronts'; insidePart = hasInsides ? 'all' : 'none'; fillCharge(); drawPart() })
        partOpenEl = open
        extras?.setLocked(null); asking?.setLocked(null)
        partBox.append(extrasLink ? el('div', { class: 'wd-part-row' }, open, extrasLink) : open)
        if (extrasLink) partBox.append(partLockLine)
        drawPartLock()
        return
      }
      partOpenEl = null
      extras?.setLocked('Part done is open'); asking?.setLocked('Part done is open')
      const whole2 = el('button', { type: 'button', class: 'link-button wd-part-close' }, 'Whole job')
      whole2.addEventListener('click', () => { part = null; otherOpen = false; drawPart() })
      partBox.append(el('div', { class: 'wd-part-head' }, el('strong', {}, 'Part done'), whole2))
      if (!hasInsides) {
        partBox.append(chipRow('What did you do?', [['fronts', 'Fronts'], ['backs', 'Backs'], ['downstairs', 'Downstairs'], ['upstairs', 'Upstairs'], ['other', 'Other']],
          (v) => (v === 'other' ? otherOpen : !otherOpen && outsidePart === v),
          (v) => { if (v === 'other') otherOpen = true; else { otherOpen = false; outsidePart = v as OutsidePart } }))
      } else {
        partBox.append(el('span', { class: 'wd-part-sub' }, 'Outsides'),
          chipRow('Outsides', [['all', 'All'], ['fronts', 'Fronts'], ['backs', 'Backs'], ['downstairs', 'Downstairs'], ['upstairs', 'Upstairs'], ['none', 'None']],
            (v) => !otherOpen && outsidePart === v, (v) => { otherOpen = false; outsidePart = v as OutsidePart }),
          el('span', { class: 'wd-part-sub' }, 'Insides'),
          chipRow('Insides', [['all', 'All'], ['downstairs', 'Downstairs'], ['upstairs', 'Upstairs'], ['none', 'None']],
            (v) => !otherOpen && insidePart === v, (v) => { otherOpen = false; insidePart = v as InsidePart }))
        const other = el('button', { type: 'button', class: `link-button wd-part-other-link${otherOpen ? ' is-on' : ''}` }, otherOpen ? 'Other: say what you did' : 'Something else? Say what you did')
        other.addEventListener('click', () => { otherOpen = true; fillCharge(); drawPart(); partOther.focus() })
        partBox.append(other)
      }
      if (otherOpen) partBox.append(partOther)
      // The owner's charge for today; on a visit with no price yet, an empty box to put one in (Codex: the owner's "how much
      // to charge" needs a place even then).
      const shown = defaultCharge()
      if (ownerPrices) partBox.append(el('label', { class: 'wd-part-charge' }, el('span', {}, `Charge today ${currencySymbol()}`), partPrice))
      else if (!pricesHidden() && shown !== null) partBox.append(el('p', { class: 'wd-part-charge' }, `Charge today ${money(shown)}`))
      restLine.textContent = restWords()
      partBox.append(restLine)
      followCash()
    }
    partOther.addEventListener('input', () => { restLine.textContent = restWords() })
    partPrice.addEventListener('input', () => { restLine.textContent = restWords(); followCash() })
    drawPart()
    fields.append(partBox)
    if (extras) fields.append(extras.panel)
    if (asking) fields.append(asking.panel)
    if (door) fields.append(el('p', { class: moneyPending(clean) ? 'credit-line credit-pending' : 'credit-line' }, door.line))
    fields.append(payment.node)
    fields.classList.add('wd-rise')
    const error = el('p', { class: 'form-error', role: 'alert' })
    const save = el('button', { type: 'submit', class: 'btn-primary wd-save' }, options.openNote ? 'Save note' : changing ? 'Save payment' : 'Save')
    // Extras ticked: Save shows the new whole ("Save · £135.00"), and Cash, if nothing has been typed in it, starts on it.
    repaintSave = (): void => {
      if (!extras || busy) return
      const t = extras.changed() ? extras.total() : null
      save.textContent = t === null ? 'Save' : `Save · ${money(t)}`
      if (askTaken && cashOnFile === null && whole !== null && startTaken !== null) payment.suggestCash?.(startTaken + ((t ?? whole) - whole))
    }
    // Close at the top does what Cancel did, so the sheet ends on one lemon Save. The button is kept off the sheet
    // because the save below still holds and words it ("Close" after an error) as before.
    const cancel = el('button', { type: 'button' }, 'Cancel')
    cancel.addEventListener('click', dismiss)
    const actions = el('footer', { class: 'booking-footer wd-foot wd-rise' }, error, save)
    // Finished by mistake: a quiet line under Save, not the first thing on the sheet.
    if (options.notFinished && !changing) {
      const back = el('button', { type: 'button', class: 'link-button wd-not-finished' }, 'Not finished yet? Put it back')
      back.addEventListener('click', () => {
        if (busy) return
        const undo = options.notFinished
        dialog.close()
        undo?.()
      })
      actions.append(back)
    }

    // "How did it go?" (Liam, 2026-09-23): everything the worker wants to pass on about the job, in one place at the
    // moment they finish it. Since 12 Oct 2026 as the started card's round buttons (Note, Photo, Voice note), each with
    // its word under it so none is missed; Note opens the box, and a voice note is typed out into it.
    const feedbackButtons = el('div', { class: 'live-tools wd-tools' })
    const photoStrip = el('div', { class: 'card-photos feedback-photos' })
    const waitLine = el('p', { class: 'feedback-wait', 'aria-live': 'polite' })
    let voiceBusy = false
    let photoBusy = false
    const holdForMedia = (): void => {
      waitLine.textContent = voiceBusy ? 'Finish the voice note first. It is typed into the note when it is done.' : photoBusy ? 'Wait a moment while the photo sends.' : ''
      save.disabled = busy || voiceBusy || photoBusy
      cancel.disabled = busy || voiceBusy
      close.disabled = busy || voiceBusy
    }
    const noteId = `wd-note-${clean.id}`
    note.id = noteId
    note.setAttribute('aria-label', 'Note for this job')
    const noteBox = el('div', { class: 'wd-note' }, note)
    const noteBtn = el('button', { type: 'button', class: 'ic ic-notes', 'aria-expanded': 'false', 'aria-controls': noteId }, 'Note')
    const showNote = (open: boolean, focus = false): void => {
      noteBox.classList.toggle('is-open', open)
      noteBtn.setAttribute('aria-expanded', String(open))
      noteBtn.classList.toggle('live-on', open)
      if (open) { fitNote(); if (focus) note.focus({ preventScroll: true }) }
    }
    noteBtn.addEventListener('click', () => showNote(!noteBox.classList.contains('is-open'), true))
    feedbackButtons.append(noteBtn)
    attachPhotos(photoStrip, {
      clean: { id: clean.id },
      buttonHost: feedbackButtons,
      idleLabel: 'Photo',
      onStateChange: (st) => { photoBusy = st === 'sending'; holdForMedia() },
    })
    const voiceBtn = el('button', { type: 'button', class: 'voice-note-btn feedback-voice' }) as HTMLButtonElement
    feedbackButtons.append(voiceBtn)
    // The voice note saves straight onto the visit (its transcript appended to
    // the stored note) and moves the visit's version on. The panel follows:
    // the new version for its own save, and the transcript added under
    // whatever has been typed, so saving the panel keeps both.
    // The note box grows with what is in it, so a long voice note is shown
    // whole instead of scrolling inside a three-line box.
    const fitNote = (): void => {
      note.style.height = 'auto'
      note.style.height = `${Math.min(note.scrollHeight + 2, 320)}px`
    }
    note.addEventListener('input', fitNote)
    // Typed in by the worker (not filled in by the panel's own voice note).
    let typed = false
    note.addEventListener('input', () => {
      typed = true
    })
    let storedNote = clean.note ?? ''
    attachVoiceNote(voiceBtn, {
      clean,
      idleLabel: 'Voice note',
      stopLabel: 'Tap to stop',
      onStateChange: (st) => { voiceBusy = st === 'recording' || st === 'sending'; holdForMedia() },
      onSaved: (row) => {
        clean.version = row.version
        clean.note = row.note
        const now = row.note ?? ''
        const added = now.startsWith(storedNote) ? now.slice(storedNote.length).trim() : now.trim()
        storedNote = now
        if (added) note.value = note.value.trim() ? `${note.value.trim()}\n${added}` : added
        if (note.value.trim()) showNote(true)
        fitNote()
      },
    })
    if (note.value.trim() || options.openNote) showNote(true, options.openNote === true)
    const feedback = el(
      'section',
      { class: 'feedback-box wd-rise', 'aria-labelledby': 'feedback-title' },
      el('h4', { id: 'feedback-title' }, 'Anything to pass on? ', el('span', { class: 'muted' }, '(optional)')),
      feedbackButtons,
      noteBox,
      photoStrip,
      waitLine,
    )
    // A finished job's Note (Today): only the note box, straight under the job, and Save note (Liam, 13 Oct 2026, on
    // practice: "When I click to add note it doesn't load up the box"). The payment stays as it is and is sent unchanged.
    if (options.openNote) {
      fields.hidden = true
      feedbackButtons.hidden = true
      note.setAttribute('rows', '5')
      // Closed (the X, a drag down, Escape) with the note changed: it saves, as Save note does, with its Undo.
      // Once only: if that save could not finish (no sign-in, refused), the next close closes, so the sheet never traps them.
      let tried = false
      saveOnClose = () => {
        if (tried || note.value.trim() === (clean.note ?? '').trim()) return false
        tried = true
        form.requestSubmit(save)
        return true
      }
      // After the sheet's own first focus (Close), the cursor goes in the box.
      setTimeout(() => note.focus({ preventScroll: true }), 50)
    }
    form.append(fields, feedback, actions)
    content.append(form)
    // A saved note opens the box: sized now it is on the page (review: sized off the page it showed as one clipped line).
    if (noteBox.classList.contains('is-open')) fitNote()
    // A finished job's Note saves the note alone (Liam, 13 Oct 2026, on practice: "I added a note after the fact but it
    // doesnt show up ... also error message after i tried to update it"): the payment it never showed is neither checked
    // nor sent again, it says Note saved, the card shows it at once, and Undo puts the old note back. A job changed
    // elsewhere while the box was open is saved again as it now is: its payment as it stands, and a note someone else
    // left meanwhile kept, with these words under it (Liam: "The someone change this note error message i dont
    // understand"), so no words are lost and nothing needs explaining.
    const saveNoteAlone = async (path: string, typed: string | null): Promise<void> => {
      busy = true; save.disabled = true; cancel.disabled = true; close.disabled = true
      let text = typed
      let before = clean.note ?? null
      const label = `Note: ${snap.customer.name} ${clean.date}`
      // With the note this box opened on (same-time saves): a job changed elsewhere in some other way still takes it.
      const send = () => mutate({ label, method: 'PATCH', path, body: { version: clean.version, note: text, base: { note: before } } })
      let ack = await send()
      if (ack.status === 409) {
        const current = ack.current as Partial<DoneClean> | undefined
        for (const q of getQueue()) if (q.path === path && q.status === 'conflict' && q.label === label) dismissQueued(q.key)
        const theirs = current && typeof current.note === 'string' ? current.note.trim() : ''
        // Words typed: saved again on the job as it now is, a note that reached it meanwhile kept on top. A note being
        // cleared is cleared again only while the job still has the note the box opened on.
        if (typeof current?.version === 'number' && (typed !== null || theirs === (before ?? '').trim())) {
          clean.version = current.version
          if (theirs && typed !== null && !typed.includes(theirs)) text = `${theirs}\n${typed}`
          before = theirs || null
          ack = await send()
        }
      }
      busy = false; cancel.disabled = false; close.disabled = false
      if (ack.ok) {
        const saved = ack.row as Partial<DoneClean> | undefined
        options.onNoteSaved?.(text, typeof saved?.version === 'number' ? saved.version : null)
        const wrapUp = (): void => {
          dialog.close()
          // The Undo bar says it (no second message on top of it).
          window.dispatchEvent(new CustomEvent('dropby:data-changed'))
          undoPatch({ text: `Note ${text ? 'saved' : 'removed'} for ${snap.customer.name}`, label: `Undo note: ${snap.customer.name} ${clean.date}`, path, ack, version: clean.version,
            body: { note: before }, after: () => window.dispatchEvent(new CustomEvent('dropby:data-changed')) })
        }
        buzz('success')
        if (reducedMotion() || typeof save.animate !== 'function') { wrapUp(); return }
        busy = true; dialog.classList.add('wd-is-saved'); save.classList.add('wd-saved'); save.textContent = 'Saved'; save.disabled = true; close.disabled = true
        window.setTimeout(wrapUp, 520)
        return
      }
      save.disabled = false
      if (ack.status === 409) {
        error.textContent = "Your note didn't save because this job changed on another phone. Close this, then tap Add a note again."
        window.dispatchEvent(new CustomEvent('dropby:data-changed'))
      } else if (ack.status === 403 && isWorker()) {
        error.textContent = 'This job has been given to someone else, so its note cannot be changed from this phone.'
        save.disabled = true
      } else if (ack.status >= 400 && ack.status < 500) {
        error.textContent = ack.error ?? "That note didn't save. Try again."
      } else if (ack.signedOut) {
        error.textContent = 'Your sign-in has expired. The note is held, not lost: sign in again and it saves itself. You can close this.'
        cancel.textContent = 'Close'
      } else {
        error.textContent = 'Waiting for signal. The note saves itself when the signal is back; you can close this.'
        cancel.textContent = 'Close'
      }
    }
    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      if (busy) return
      if (voiceBusy || photoBusy) { holdForMedia(); return }
      // The tap itself: an iPhone only gives its light tap for a person's tap, not after the save comes back.
      buzz('light')
      error.textContent = ''
      const path = `/api/cleans/${clean.id}`
      // A Start, Finish or note for this job still waiting for signal is not a
      // reason to refuse (worker review 2026-09-24: Finish in a dead zone then
      // blocked the payment, and closing the panel lost it). The queue sends
      // this save after it, with the version that one produced (api.ts
      // chainVersion). Only a change that came back refused or in conflict,
      // or one held for sign-in, holds this one.
      const blocked = getQueue().find((q) => q.path === path && q.status !== 'sending' && q.status !== 'queued')
      if (blocked) {
        error.textContent = blocked.status === 'sending' || blocked.status === 'queued'
          ? 'A change for this visit is still waiting to save. Check the saving indicator before adding another.'
          : blocked.status === 'signed-out'
            ? 'An earlier change to this visit is held until you sign in again. Deal with it in the saving indicator at the bottom of the screen first.'
            : `An earlier change to this visit needs your attention before this one can save (${blocked.status === 'conflict' ? 'it was edited elsewhere' : blocked.error ?? 'it was rejected'}). Open the saving indicator at the bottom of the screen.`
        return
      }
      // What reached the job since the panel last knew it (clean.note is the
      // card's own copy, kept up to date by its voice note), added once.
      const withArrived = (text: string): string => {
        const now = clean.note ?? ''
        const arrived = now.startsWith(storedNote) ? now.slice(storedNote.length).trim() : ''
        return arrived && !text.includes(arrived) ? (text ? `${text}\n${arrived}` : arrived) : text
      }
      if (options.openNote) { await saveNoteAlone(path, withArrived(note.value.trim()) || null); return }
      if (!payment.value) {
        error.textContent = 'Say whether it was paid: No or Yes.'
        flagMissingPayment(payment)
        return
      }
      // Part done: what was done (outsides and insides, a half, or their own words), and (the owner) today's charge, never
      // more than the whole job. The server makes the words from the two halves (functions/lib/part-done.ts planPart).
      let partDone: { part?: string; outside?: OutsidePart; inside?: InsidePart; price_pence?: number } | null = null
      if (part !== null && partAllowed) {
        if (otherOpen) {
          const said = partOther.value.trim().toLowerCase()
          if (!said) { error.textContent = 'Say what you did, for example front and sides.'; partOther.focus(); return }
          partDone = { part: said }
        } else {
          if (view().whole) { error.textContent = "That's the whole job: tap Whole job, or pick what wasn't done."; return }
          if (view().nothing) { error.textContent = "Nothing was done: use Couldn't do it on the job instead."; return }
          partDone = { outside: outsidePart, inside: hasInsides ? insidePart : 'none' }
        }
        if (ownerPrices) {
          let charge: number | null = null
          try { charge = parseMoney(partPrice.value) } catch (err) { error.textContent = (err as Error).message; partPrice.focus(); return }
          if (charge === null && whole !== null) { error.textContent = 'Put in what you charged for today.'; partPrice.focus(); return }
          if (charge !== null && whole !== null && charge > whole) { error.textContent = `That is more than the whole job (${money(whole)}).`; partPrice.focus(); return }
          if (charge !== null && charge !== defaultCharge()) partDone.price_pence = charge
        }
      }
      const extrasProblem = extras?.problem() ?? asking?.problem() ?? null
      if (extrasProblem) { error.textContent = extrasProblem; return }
      const extrasChange = extras?.value() ?? null
      const cash = (askCash || askTaken) && payment.value === 'cash' ? payment.cashPence() : null
      if ((askCash || askTaken) && payment.value === 'cash' && (cash === null || Number.isNaN(cash))) {
        error.textContent = cash === null ? 'Enter how much cash you took.' : `Enter the cash in ${currencyWord()}, for example 60 or 60.50.`
        flagMissingPayment(payment)
        return
      }
      busy = true; save.disabled = true; fields.disabled = true; cancel.disabled = true; close.disabled = true
      // Where Save was tapped: the payment message afterwards opens only there (Astra).
      const savedFrom = { hash: window.location.hash, partition: queuePartition() }
      const method = ['cash', 'bank'].includes(payment.value) ? payment.value : null
      const body = {
        version: clean.version,
        state: 'done' as const,
        payment_status: method || payment.value === 'paid' ? 'paid' : payment.value,
        payment_method: method,
        // The cash taken: always on a first payment; on a visit already paid in cash only when the figure changed.
        ...(cash !== null ? (askTaken ? (cashOnFile === null || cash !== cashOnFile || clean.payment_method !== 'cash' ? { taken_pence: cash } : {}) : { cash_collected_pence: cash }) : {}),
        // The day it was paid rides on the change, so a save sent later from the queue keeps it (0043).
        ...(getMe()?.broughtMoney === true && method ? { paid_on: todayISO() } : {}),
        // The note goes only if the worker typed in it (review 2026-09-24):
        // words that reached the job while the panel was open (a voice note on
        // the card still being typed out, or one sent by itself when the
        // signal came back) are on the job already, and sending the box as it
        // was opened would wipe them. Typed, those words are added to it.
        ...(typed ? { note: withArrived(note.value.trim()) || null } : {}),
        // Part done: today's part and price, and the rest booked (functions/lib/part-done.ts).
        ...(partDone ? { part_done: partDone } : {}),
        // Extras ticked on Job done (src/done-extras.ts): the whole price, its insides part and its extras, in the same save.
        ...(extrasChange ? { amount_pence: extrasChange.amount_pence, inside_pence: extrasChange.inside_pence, extras_json: extrasChange.extras_json } : {}),
        // A helper's extras: what they did, for the owner to price (0091).
        ...(asking?.asked() ? { extras_asked: asking.asked() } : {}),
      }
      const label = `${changing ? 'Payment' : 'Log'}: ${snap.customer.name} ${clean.date}`
      // Already in Owed before this save: done and not paid, or never recorded (both are counted there).
      const wasOwed = clean.state === 'done' && clean.payment_status !== 'paid'
      const ack = await mutate({ label, method: 'PATCH', path, body })
      busy = false; cancel.disabled = false; close.disabled = false
      if (ack.ok) {
        // Saved: the lemon Save turns green with a tick and the phone taps, then the sheet slides away and the job's
        // card turns green behind it (12 Oct 2026). At once with reduced motion.
        const wrapUp = (): void => {
          dialog.close()
          // Cash over what was due is kept as credit (0043, Liam Q2): said here, as the queue says it for a later send.
          const kept = ack.money?.kept_as_credit_pence ? `. ${money(ack.money.kept_as_credit_pence)} kept as credit for their next clean` : ''
          // Part done says what happened to the rest (functions/lib/part-done.ts).
          const saidPart = partDone ? (partDone.part !== undefined ? { part: partDone.part, rest: 'The rest' } : { part: view().part, rest: partLabel(view().restPart) }) : null
          const partWords = saidPart ? `${partLabel(saidPart.part)} done. ${saidPart.rest} booked for this week, no day yet` : null
          toast(`${partWords ?? (changing ? 'Payment changed' : ack.nextBooked ? nextBookedMessage(ack.nextBooked) : 'Completed work recorded')}${kept}${asking?.asked() ? '. Extras sent to the owner to price' : ''}`)
          window.dispatchEvent(new CustomEvent('dropby:data-changed'))
          // Back to booked, money fields cleared. A next visit the server
          // suggested from the cycle stays as a suggestion; it is not a booking.
          // A worker login may only mark a visit done, never put it back, so
          // no Undo is offered there (it would be refused every time).
          // Part done afterwards (Change payment): Undo makes it the whole job again at its price, its rest taken out.
          if (!isWorker() && changing && partDone) undoPatch({ text: `${snap.customer.name}: ${partDone.part} only`, label: `Undo part done: ${snap.customer.name} ${clean.date}`, path, ack, version: clean.version,
            body: { part: null, amount_pence: clean.amount_pence ?? null, inside_pence: clean.inside_pence ?? null, note: clean.note ?? null }, after: () => window.dispatchEvent(new CustomEvent('dropby:data-changed')) })
          const offerUndoDone = (link?: UndoLink): void => undoPatch({ text: `Marked ${snap.customer.name} done`, label: `Undo done: ${snap.customer.name} ${clean.date}`, path, ack, version: clean.version,
            // A part done undone: the whole price back too (the server takes the part off and withdraws the rest it booked).
            body: { state: 'booked', confirmed: clean.confirmed ?? 0, time_agreed_for: timeIsAgreed(clean) ? clean.time_agreed_for : null, note: clean.note ?? null,
              ...(partDone || extrasChange ? { amount_pence: clean.amount_pence ?? null, inside_pence: clean.inside_pence ?? null } : {}),
              // Extras added on Done come off again with it (the visit as it was booked).
              ...(extrasChange ? { extras_json: extrasOf(clean.extras_json).length ? extrasOf(clean.extras_json) : null } : {}) }, after: () => window.dispatchEvent(new CustomEvent('dropby:data-changed')), ...(link ? { link } : {}) })
          // A commercial customer's visit saved not paid, on the owner's phone: "Send invoice" beside Undo (0080; Liam,
          // 20 Oct 2026; docs/plans/commercial/AGREED.md 14). A worker's phone asks nothing more.
          // Undo at once; "Send invoice" joins it when their page has been read, if the bar is still the one offered
          // (the recheck's S5).
          const savedUnpaid = (ack.row as { payment_status?: string } | undefined)?.payment_status === 'unpaid'
          if (!isWorker() && !changing) {
            offerUndoDone()
            const offered = document.querySelector('#undo-bar')
            const invoiceLink = (): void => {
              if (!savedUnpaid || !invoicesHere() || !options.customerId) return
              void invoiceAfterJobLink(options.customerId, clean.id).then((link) => {
                if (link && offered && offered.isConnected && document.querySelector('#undo-bar') === offered) offerUndoDone(link)
              }, () => undefined)
            }
            // Into Owed just now, the whole job: the payment message comes straight up over the bar (Liam, 21 Oct 2026,
            // picture A: "only when its not a worker ... if you own the round and are working it"), as Owed's Done does
            // after Not paid yet, with the Done's Undo again once it closes. A commercial customer keeps Send invoice.
            if (intoOwed && !partDone && ack.row && options.customerId) {
              const before = { ...clean, customer_id: options.customerId, customer_name: snap.customer.name,
                customer_address: addressLine(snap.customer.address, snap.customer.postcode, ', ') } as unknown as Clean
              // Still where Save was tapped, and its Undo not used (Astra: nothing opens over another screen or a visit
              // put back meanwhile).
              const stillOn = (): boolean => window.location.hash === savedFrom.hash && queuePartition() === savedFrom.partition
                && !!offered && offered.isConnected && document.querySelector('#undo-bar') === offered
              if (stillOn()) void import('./screens/owed').then(({ openPaymentAfterDone }) => openPaymentAfterDone(before, ack, stillOn, invoiceLink), invoiceLink)
              else invoiceLink()
            } else invoiceLink()
          }
        }
        // No, saved not paid, on an owner's phone, and in Owed only now: the Save says so, orange with a clock (tidy-up
        // item 2; Liam, 20 Oct 2026). A worker has no Owed; one already in it, or saved paid from credit, says Saved.
        const intoOwed = !isWorker() && !wasOwed && payment.value === 'unpaid' && (ack.row as { payment_status?: string } | undefined)?.payment_status === 'unpaid'
        buzz(intoOwed ? 'light' : 'success')
        if (reducedMotion() || typeof save.animate !== 'function') { wrapUp(); return }
        // Held while it shows Saved: nothing on the sheet can save or close it twice.
        busy = true; dialog.classList.add('wd-is-saved'); save.classList.add('wd-saved'); save.textContent = intoOwed ? 'Added to Owed' : 'Saved'; save.disabled = true; close.disabled = true
        if (intoOwed) save.classList.add('wd-saved-owed')
        window.setTimeout(wrapUp, 520)
      } else if (ack.status === 409) {
        // The owner changed this job while the panel was open. "Close and
        // reopen" used to be the advice, but reopening carried the same stale
        // version and the parked conflict then blocked every save (worker
        // review 2026-09-24). Take the job's current version, clear this
        // save's parked conflict, keep the answers, and let Save go again.
        for (const q of getQueue()) if (q.path === path && q.status === 'conflict' && q.label === label) dismissQueued(q.key)
        const current = ack.current as Partial<DoneClean> | undefined
        if (typeof current?.version === 'number') clean.version = current.version
        if (current && (typeof current.note === 'string' || current.note === null)) clean.note = current.note as string | null
        // The retry is based on the reviewed arrangement too. Otherwise its
        // later Undo could restore an old confirmation key onto the new time.
        if (typeof current?.date === 'string') clean.date = current.date
        if (current && (typeof current.time_slot === 'string' || current.time_slot === null)) clean.time_slot = current.time_slot
        if (current?.confirmed === 0 || current?.confirmed === 1) clean.confirmed = current.confirmed
        if (current && (typeof current.time_agreed_for === 'string' || current.time_agreed_for === null)) clean.time_agreed_for = current.time_agreed_for
        visitSummary.textContent = visitWords()
        if (current?.state === 'done') {
          clean.state = 'done'; changing = true
          heading.textContent = 'Change payment'; save.textContent = 'Save payment'
        }
        fields.disabled = false; save.disabled = false
        error.textContent = 'This job was changed while you had it open. Your answers are still here: check them and save again.'
        window.dispatchEvent(new CustomEvent('dropby:data-changed'))
      } else if (ack.status === 403 && isWorker()) {
        // Given to someone else while the panel was open (review 2026-09-24).
        error.textContent = 'This job has been given to someone else, so it cannot be logged from this phone. Close this panel.'
        cancel.textContent = 'Close'
        window.dispatchEvent(new CustomEvent('dropby:data-changed'))
      } else if (ack.status >= 400 && ack.status < 500) {
        fields.disabled = false; save.disabled = false
        error.textContent = ack.error ?? 'Check the fields and try again.'
      } else if (ack.signedOut) {
        error.textContent = 'Your sign-in has expired. This visit is held, not lost: sign in again and it saves itself. You can close this panel.'
        cancel.textContent = 'Close'
      } else {
        error.textContent = 'Waiting for signal. This change is in the retry queue; you can close this panel.'
        cancel.textContent = 'Close'
      }
    })
    payment.focus()
  }

  async function chooseCustomer(): Promise<void> {
    content.textContent = 'Loading customers…'
    try {
      const data = await api<{ customers: Customer[] }>('/api/customers')
      if (closed) return
      clear(content)
      const search = el('input', { type: 'search', placeholder: postcodeWords('Name, address or postcode'), 'aria-label': 'Find customer' })
      const list = el('div', { class: 'customer-picker' })
      // Someone new (Liam, 19 Oct 2026: "Book a visit should give you the option of a new customer not just select a
      // current one"): the first job's steps for them (first-job.ts), the customer and the visit in one save, the name
      // typed here filled in and the day this was opened on picked. Not when logging work already done. Book a visit stays
      // open until the steps can open: one still waiting for signal says so here, with the list and the search kept.
      const someoneNew = (seed: { name?: string; address?: string; postcode?: string } = {}): void => {
        void import('./first-job').then((m) => {
          if (closed) return
          if (m.firstJobWaiting()) { toast('Your last booking is still waiting for signal. It saves as soon as it can.'); return }
          dialog.close()
          void m.openFirstJob({ someoneNew: true, ...seed, date: options.date, customers: data.customers })
        })
      }
      // What was typed goes in the box it looks like: a postcode (or US ZIP), an address (it has a number), or a name.
      const seedFor = (typed: string): { name?: string; address?: string; postcode?: string } =>
        /^[A-Z]{1,2}\d[A-Z\d]?(\s*\d[A-Z]{2})?$/i.test(typed) || /^\d{5}(-\d{4})?$/.test(typed) ? { postcode: typed.toUpperCase() }
          : /\d/.test(typed) ? { address: typed } : { name: typed }
      const canAdd = !options.completed
      const draw = (): void => {
        clear(list)
        const matches = findCustomers(data.customers, search.value)
        for (const c of matches.slice(0, 30)) {
          const pick = el('button', { type: 'button', class: 'customer-choice' }, el('strong', {}, c.name), el('span', {}, [addressLine(c.address, c.postcode, ' · '), standingWords(c)].filter(Boolean).join(' · ')))
          pick.addEventListener('click', () => void loadCustomer(c.id))
          list.append(pick)
        }
        const typed = search.value.trim()
        if (!matches.length && typed && canAdd) {
          const seed = seedFor(typed)
          const add = el('button', { type: 'button', class: 'btn-secondary booking-add-typed' }, seed.name ? `+ Add ${typed} as a new customer` : `+ New customer at ${typed}`)
          add.addEventListener('click', () => someoneNew(seed))
          list.append(el('p', {}, `Nobody on your round matches "${typed}".`), add)
        } else if (!matches.length) list.append(el('p', {}, postcodeWords('No customers match. Try another name or postcode.')))
        if (matches.length > 30) list.append(el('p', { class: 'muted' }, 'Type to narrow the list.'))
      }
      search.addEventListener('input', draw)
      if (canAdd) {
        const fresh = el('button', { type: 'button', class: 'btn-primary booking-new-customer' }, '+ New customer')
        fresh.addEventListener('click', () => someoneNew())
        content.append(search, fresh, ...(data.customers.length ? [el('p', { class: 'booking-pick-caption' }, 'Or pick someone already on your round')] : []), list)
      } else content.append(search, list)
      // Ready to type on a computer; on a phone tapped once the panel is up (sheet-keyboard.ts, 20 Oct 2026).
      draw(); cursorOnOpen(search as HTMLInputElement)
      // A one-person round has no who-does-it step (Liam, 19 Oct 2026): three steps, not four.
      guideTo(`1 of ${worksAlone() ? 3 : 4}: Pick who the visit is for. Tap their name.`, list)
    } catch (err) { showLoadError(err, chooseCustomer) }
  }

  function showLoadError(err: unknown, retry: () => Promise<void>): void {
    if (closed) return
    clear(content)
    const button = el('button', { type: 'button' }, 'Try again')
    button.addEventListener('click', () => void retry())
    content.append(el('p', { role: 'alert' }, `Couldn't load: ${(err as Error).message}`), button)
  }

  async function loadCustomer(id: number): Promise<void> {
    content.textContent = 'Loading visits…'
    try {
      // Several jobs on: the customer's jobs too, so the editor books the right one.
      const on = severalJobsOn()
      const [data, jobData] = await Promise.all([
        api<{ customer: Customer; cleans: Clean[]; quote_choice?: BookingQuote | null }>(`/api/customers/${id}`),
        // Never an empty list in place of one that failed to load (Astra r2, finding 6): the copy kept from the last
        // read, or no editor at all (the load error offers Try again). An editor that has not read the customer's
        // jobs must not save as one that has.
        on
          ? api<{ jobs: CustomerJob[] }>(`/api/customers/${id}/jobs`, `customer-jobs-${id}`).catch((err: unknown) => {
              const kept = readCache<{ jobs: CustomerJob[] }>(`customer-jobs-${id}`)?.data
              if (kept && Array.isArray(kept.jobs)) return kept
              throw new Error(`Couldn't load this customer's jobs (${(err as Error).message}). Try again.`)
            })
          : Promise.resolve(null),
      ])
      if (options.linkLead) await readBookingQuote(options.linkLead)
      customerQuote = data.quote_choice ?? null
      if (closed) return
      const existing = options.cleanId ? data.cleans.find(c => c.id === options.cleanId) : undefined
      if (options.cleanId && !existing) throw new Error('This visit was removed. Close the editor and reload the screen.')
      const future = data.cleans.filter(c => c.state === 'booked' && c.date >= todayISO()).sort((a, b) => a.date.localeCompare(b.date))
      let selected = existing ?? (!options.completed ? future[0] : undefined)
      let pick: JobPick | null = null
      if (jobData) {
        const extra = jobData.jobs.filter((j) => !j.deleted_at)
        pick = { extra, jobs: [jobOneOf(data.customer), ...extra.map(extraJobOf)] }
        // It opens on THAT job's next visit, never another job's (PLAN 3).
        if (!existing && !options.completed) {
          const want = options.jobId ?? null
          selected = future.filter((c) => visitJobId(c) === want && !isContact(c)).sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)[0]
        }
      }
      if ((options.replyConfirmation || options.linkLead && options.acceptedOffer && !options.addOfferToVisit) && !existing) selected = undefined
      // Today's visit for the job being logged is the work being logged, said in words with "Log a separate visit
      // instead" beside it. Any other visit still open (an earlier day, another job, one marked couldn't do) is
      // offered on the form, never chosen silently (Astra, 8 Oct 2026).
      if (options.completed && !existing) {
        const today = todayISO()
        selected = openVisits(data.cleans).find((c) => c.date === today && !c.blocked_reason && visitJobId(c) === (options.jobId ?? null))
        finishing = selected?.id ?? null
      }
      renderForm(data.customer, data.cleans, selected, false, pick)
    } catch (err) { showLoadError(err, () => loadCustomer(id)) }
  }

  function renderForm(customer: Customer, cleans: Clean[], clean: Clean | undefined, additional: boolean, pick: JobPick | null = null): void {
    if (busy) return
    clear(content)
    const done = options.completed === true
    // The presentations of the one editor (Liam approved the redesign, 9 Oct 2026; canvas VKDK3zCXygGF8dgMxq593n): a
    // NEW visit is drawn as the canvas's New artboard ("Book Elm", the day buttons, the switches, the lemon button that
    // names the day); EDIT as its Edit artboard (the saved visit's words, the move warning, "Move to Mon 12 Oct"); an
    // accepted REPLY the same parts with its own words and behaviour; Log completed work (DONE) keeps its own form.
    const presentation: 'new' | 'edit' | 'reply' | 'done' = done ? 'done' : options.replyConfirmation ? 'reply' : clean ? 'edit' : 'new'
    const fresh = presentation === 'new'
    const newLook = presentation !== 'done'
    const first = firstName(customer.name)
    heading.textContent = done ? 'Log completed work' : options.replyConfirmation && (options.agreedDate || clean?.confirmed === 1) ? 'Book agreed visit' : clean ? 'Edit visit' : fresh && first ? `Book ${first}` : 'Book a visit'
    const whoLine = el('p', { class: 'muted booking-who-line' }, addressLine(customer.address, customer.postcode, ' · '))
    content.append(el('h3', {}, customer.name), whoLine)
    // Pin the exact spot, under their address (27 Oct 2026, exact-spot.ts): someone already on the round only.
    if (customer.id > 0) {
      const spotFor = (link: string | null): HTMLElement => spotButton({ id: customer.id, name: customer.name, link }, (now) => {
        if (now === undefined) return
        customer.maps_link = now
        spot.replaceWith(spot = spotFor(now))
      })
      let spot = spotFor(customer.maps_link ?? null)
      content.append(spot)
    }
    // Book them in: nothing is saved until Book, and a customer of theirs already on the round is one tap away.
    const enquiry = options.enquiry ?? null
    const otherJobs=enquiry?otherJobsChoice(options.draft?.otherJobs??null):null
    if (options.offersDeferred) content.append(el('p', { class: 'booking-offers-deferred muted' }, 'Offered extras can be added once you have signal'))
    if (options.linkLead && !clean) {
      content.append(el('p', { class: 'booking-enquiry-note' }, `Nothing is saved until you tap Book. Then ${firstName(options.linkLead.name) || options.linkLead.name}'s enquiry goes on ${first || customer.name}'s page.`))
    }
    if (enquiry) {
      const notice = el('p', { class: 'booking-enquiry-note' }, `Nothing is saved until you tap Book. ${first || customer.name} becomes a customer then.`)
      if (options.pickCustomer) {
        const pick = el('button', { type: 'button', class: 'link-button' }, 'Already a customer? Pick them')
        pick.addEventListener('click', () => {
          const back = options.pickCustomer
          const draft: BookDraft = { date: date.value, time: time.value, first: price.value, regular: enquiryParts.regular.value, every: enquiryParts.every.value,
            postcode: enquiryParts.postcode.value, worker: worker.value, duration: duration.value, note: note.value, extras: extras.value(), otherJobs:otherJobs?.value()??null }
          dialog.close()
          back?.(draft)
        })
        notice.append(' ', pick)
      }
      content.append(notice)
    }
    // Edit says the visit as it is saved, in the shared words, so the switches below can be read against it.
    if (presentation === 'edit' && clean) {
      const shownTime = shownVisitTime(clean)
      const when = `${dayLabel(clean.date)}${shownTime ? `, ${shownTime}` : ''}`
      content.append(el('p', { class: 'muted booking-saved-as' }, `Saved as: ${when} · ${visitWords(clean, customer).text}${pricesHidden() ? '' : ` · ${priceWords(clean) ?? 'not priced'}`}`))
    }
    // Where they stand in the shared words (visit-words.ts standingWords), not the stored status: Recanvass and Taken
    // off customers read "paused" here (bug hunt 8 Oct 2026).
    if (customer.status !== 'active') content.append(el('p', { class: 'notice' }, `${standingWords(customer) || 'Not on the round'}: check that they want this visit.`))
    const notes = [customer.job_notes, customer.parking_restrictions && `Parking: ${customer.parking_restrictions}`, customer.preferred_times && `Preferred times: ${customer.preferred_times}`].filter(Boolean)
    if (notes.length) content.append(el('div', { class: `booking-access${newLook ? ' booking-access-new' : ''}` }, ...notes.map(n => el('p', {}, n))))
    // Their note (the customer page's Notes), one line with More to read it all: what the owner knows about them is
    // in front of them while choosing the day.
    if (newLook && customer.notes?.trim()) {
      const text = el('span', { class: 'booking-their-note-text' }, customer.notes.trim())
      const box = el('div', { class: 'booking-their-note' }, el('strong', {}, 'Note: '), text)
      if (customer.notes.trim().length > 60 || customer.notes.includes('\n')) {
        const more = el('button', { type: 'button', class: 'link-button booking-their-note-more', 'aria-expanded': 'false' }, 'More')
        more.addEventListener('click', () => {
          const open = !box.classList.contains('booking-their-note-open')
          box.classList.toggle('booking-their-note-open', open)
          more.setAttribute('aria-expanded', String(open)); more.textContent = open ? 'Less' : 'More'
        })
        box.append(' ', more)
      }
      content.append(box)
    }
    const future = cleans.filter(c => c.state === 'booked' && c.date >= todayISO()).sort((a, b) => a.date.localeCompare(b.date))
    // Several jobs: what each visit is for, by its job's name.
    const jobOfVisit = (cl: Clean): Job | undefined => pick?.jobs.find((j) => j.id === visitJobId(cl))
    const visitWord = (cl: Clean): string => {
      const j = jobOfVisit(cl)
      return pick && j ? (j.name?.trim() || kindLabel(j.id === null ? cl.kind : j.kind)) : kindLabel(cl.kind)
    }
    if (options.replyConfirmation) {
      // A reply's chosen day is the current task. The generic Edit-old-date
      // shortcut used to discard it when the owner tried to finish the pencil.
      const agreed = options.agreedDate ?? (clean?.confirmed === 1 ? clean.date : undefined)
      if (agreed) {
        const context = el('div', { class: 'notice booking-agreed-date' }, el('strong', {}, `Customer accepted ${dayLabel(agreed)}`))
        if (clean && clean.date !== agreed && !clean.confirmed) context.append(el('p', {}, `Saving moves the visit on ${dayLabel(clean.date)} to this agreed day. You do not need to add another visit.`))
        content.append(context)
      }
      const others = future.filter(cl => cl.id !== clean?.id)
      if (others.length) {
        const existingBox = el('div', { class: 'notice' }, el('strong', {}, 'Other visits on the diary'))
        for (const cl of others) existingBox.append(el('p', {}, `${dayLabel(cl.date)} · ${visitWord(cl)} · ${cl.confirmed ? 'agreed' : 'not agreed'}`))
        // A separate visit is still an explicit choice when another agreed
        // booking is retained. It cannot replace the reply's selected date.
        if (!clean && !additional && !done) {
          const another = el('button', { type: 'button' }, 'Add a separate visit instead')
          another.addEventListener('click', () => renderForm(customer, cleans, undefined, true, pick))
          existingBox.append(another)
        }
        if (additional) existingBox.append(el('p', {}, 'Adding a separate visit. Existing bookings will stay in place.'))
        content.append(existingBox)
      }
    } else if (done && !options.cleanId) {
      // Log completed work: which booked visit this finishes, and the other visits still open, each one tap away.
      if (clean && clean.id === finishing) {
        const box = el('div', { class: 'notice booking-finishing' }, el('strong', {}, `This finishes the visit booked for ${dayLabel(clean.date)}`))
        const separate = el('button', { type: 'button' }, 'Log a separate visit instead')
        separate.addEventListener('click', () => { finishing = null; renderForm(customer, cleans, undefined, true, pick) })
        box.append(separate)
        content.append(box)
      }
      const others = openVisits(cleans).filter((cl) => cl.id !== clean?.id)
      if (others.length) {
        const box = el('div', { class: 'notice' }, el('strong', {}, clean ? 'Also still open on the diary' : 'Still open on the diary'))
        for (const cl of others) {
          const finish = el('button', { type: 'button' }, `Finish ${dayLabel(cl.date)} · ${visitWord(cl)}`)
          finish.addEventListener('click', () => { finishing = cl.id; renderForm(customer, cleans, cl, false, pick) })
          box.append(finish)
        }
        content.append(box)
      }
    } else if (presentation === 'edit') {
      // Edit opens straight on the visit (canvas artboard Edit): never "Already on the diary" beside itself (roundly-bugs,
      // 9 Oct 2026). Their other visits stay one tap away, and a separate visit too, where the form always offered it.
      const others = future.filter((cl) => cl.id !== clean?.id)
      if (others.length) {
        const otherBox = el('div', { class: 'notice booking-already' }, el('strong', {}, 'Also on the diary'))
        for (const cl of others) {
          const edit = el('button', { type: 'button' }, `Edit ${dayLabel(cl.date)} · ${visitWord(cl)} · ${priceWords(cl) ?? money(cl.amount_pence)}`)
          edit.addEventListener('click', () => { options.completed = false; options.agreedDate = undefined; renderForm(customer, cleans, cl, false, pick) })
          if (options.acceptedOffer) otherBox.append(el('p', {}, edit.textContent?.replace(/^Edit /, '') ?? ''))
          else otherBox.append(edit)
        }
        content.append(otherBox)
      }
      if (future.length && !additional) {
        const another = el('button', { type: 'button', class: 'link-button booking-separate' }, 'Add a separate visit instead')
        another.addEventListener('click', () => renderForm(customer, cleans, undefined, true, pick))
        content.append(el('p', { class: 'booking-separate-line' }, another))
      }
    } else if (future.length) {
      const existingBox = el('div', { class: 'notice booking-already' }, el('strong', {}, 'Already on the diary'))
      for (const cl of future) {
        const edit = el('button', { type: 'button' }, `Edit ${dayLabel(cl.date)} · ${visitWord(cl)} · ${priceWords(cl) ?? money(cl.amount_pence)}`)
        edit.addEventListener('click', () => { options.completed = false; options.agreedDate = undefined; renderForm(customer, cleans, cl, false, pick) })
        if (options.acceptedOffer) existingBox.append(el('p', {}, edit.textContent?.replace(/^Edit /, '') ?? ''))
        else existingBox.append(edit)
      }
      if (!additional && !done) {
        const another = el('button', { type: 'button' }, 'Add a separate visit instead')
        another.addEventListener('click', () => renderForm(customer, cleans, undefined, true, pick))
        existingBox.append(another)
      }
      if (additional) existingBox.append(el('p', {}, 'Adding a separate visit. Existing bookings will stay in place.'))
      content.append(existingBox)
    }
    const form = el('form', { class: 'booking-form' })
    const fields = el('fieldset', { class: 'booking-fields' })
    const field = (label: string, node: HTMLElement): HTMLElement => el('label', { class: 'booking-field' }, el('span', {}, label), node)
    const date = el('input', { type: 'date', required: 'required' })
    // Several jobs: the job this visit is for. An existing visit keeps its own; a new one starts on the job asked
    // for (job 1 when none was), or job 1's way of working when several jobs is off (pick null).
    let job: Job | null = pick ? (clean ? jobOfVisit(clean) ?? pick.jobs[0]! : pick.jobs.find((j) => j.id === (options.jobId ?? null) && !j.stopped_on) ?? pick.jobs.find((j) => !j.stopped_on) ?? pick.jobs[0]!) : null
    // A visit's date and price come from ITS job's own visits only (PLAN 2d): job 1's last visit for the windows,
    // never the gutters'. Off, every visit is job 1's and this is the customer's last visit, as before.
    const jobVisits = (j: Job | null): Clean[] => (j ? visitsOf({ id: j.id, customer_id: customer.id }, cleans) : cleans)
    const last = jobVisits(job && job.id === null ? job : pick ? jobOneOf(customer) : null).filter(c => c.state === 'done').sort((a,b) => b.date.localeCompare(a.date))[0]?.date
    const jobDate = (j: Job): string => {
      const today = todayISO()
      const next = visitsOf({ id: j.id, customer_id: customer.id }, cleans).filter((c) => c.state === 'booked' && c.date >= today && !isContact(c)).sort((a, b) => a.date.localeCompare(b.date))[0]
      if (next) return next.date
      const lastDone = lastDoneOf({ id: j.id, customer_id: customer.id }, cleans)
      return lastDone && j.cycle_days ? nextAfterDone(lastDone, j.cycle_days, today) : today
    }
    date.value = options.agreedDate ?? clean?.date ?? options.date ?? (done ? todayISO() : job && job.id !== null ? jobDate(job) : nextPlanningDate({ ...customer, last_clean: last }))
    if (done) date.max = todayISO()
    // A new visit never goes on a day that has gone (docs/DIARY-AND-MESSAGES.md section 9): the days start at today.
    // An edit keeps the day it is saved on, even one that has gone; it only moves to today or later.
    if (newLook) date.min = clean && clean.date < todayISO() ? clean.date : todayISO()
    if (fresh) {
      if (date.value < todayISO()) date.value = todayISO()
      const every = cycleLabel(customer.cycle_days, customer.cycle_text)
      whoLine.textContent = [addressLine(customer.address, customer.postcode, ' · '), every ? (/^\d/.test(every) ? `every ${every}` : every) : '', last ? `last clean ${shortDate(last)}` : ''].filter(Boolean).join(' · ')
    }
    const time = el('input', { type: clean?.time_slot && !/^\d{2}:\d{2}$/.test(clean.time_slot) ? 'text' : 'time' })
    time.value = clean?.time_slot ?? ''
    const worker = el('input', { list: 'booking-workers', placeholder: 'Unassigned', maxlength: '100' })
    worker.value = clean?.worker ?? ''
    // A one-person round (Liam, 19 Oct 2026): no "Who's doing it?", unless the visit already carries a name that is not
    // the owner's, so it can be seen and cleared. Hidden, the name box still sends what it holds, so an edit keeps it.
    const alone = worksAlone()
    const ownName = getMe()?.workerName ?? ''
    const askWho = !alone || !!otherThanOwner(clean?.worker, ownName)
    // What a save sends as the name (Sol's and Sonnet's review, 19 Oct 2026: a one-person round's server refuses any name
    // but the owner's): blank or the owner's own, as before; someone else's is never sent, so a visit carrying one keeps
    // it as it is and an Undo leaves it alone.
    const nameSent = (v: string | null): { worker?: string | null } => (alone && v && otherThanOwner(v, ownName) ? {} : { worker: v })
    // The name said beside a visit on the day (" · Sendi"): in a one-person round only someone else's, else nothing.
    const visitName = (c: Clean, fallback: string): string => {
      if (!alone) return ` · ${c.worker ?? fallback}`
      const other = otherThanOwner(c.worker, ownName)
      return other ? ` · ${other}` : ''
    }
    // No seed names here: the datalist starts empty and preview() below
    // fills it from the day's own workers as soon as it has read one — no
    // business's staff belongs hardcoded in another's copy of this file.
    const workers = el('datalist', { id: 'booking-workers' })
    const duration = el('input', { type: 'number', min: '1', max: '1440', step: '1', placeholder: 'Unknown', inputmode: 'numeric' })
    duration.value = clean?.duration_minutes ? String(clean.duration_minutes) : ''
    const kind = el('select', {})
    for (const [value, label] of [['window', kindLabel('window')], ['gutter', kindLabel('gutter')], ['other', kindLabel('other')]]) kind.append(el('option', { value }, label))
    kind.value = options.customerOffer ? options.acceptedOffer?.offer.items.some(e => e.kind === 'gutters') ? 'gutter' : 'other' : clean?.kind ?? 'window'
    if(options.customerOffer)kind.disabled=true
    // A different kind of work for the customer (Liam Q2): a NEW job, a one-off unless the owner makes it regular.
    // Never booked as job 1, so it never resets the windows' clock or books windows off itself (PLAN 2d).
    let newJob: { kind: JobKind } | null = null
    const price = el('input', { inputmode: 'decimal', placeholder: 'Not priced yet' })
    // Book them in: what they pay after the first clean, how often, and the postcode (checked here, as Make them a
    // customer checks it), each starting from the enquiry. Drawn under the price (layoutNew).
    const enquiryParts = {
      regular: el('input', { inputmode: 'decimal', placeholder: 'Not priced yet', 'aria-label': 'Then each time' }) as HTMLInputElement,
      every: el('select', { 'aria-label': 'How often' }) as HTMLSelectElement,
      postcode: el('input', { type: 'text', maxlength: '20', autocomplete: 'off', 'aria-label': postcodeWords('Postcode') }) as HTMLInputElement,
    }
    // Just once or Ad hoc picked on Book them in (How often design D), and the tile itself (Regular before its number).
    let enquiryMarker: string | null = enquiry ? (cycleLabel(null, customer.cycle_text) === 'just once' ? 'Just once' : cycleLabel(null, customer.cycle_text) === 'when they ask' ? 'Ad-hoc' : null) : null
    let enquiryKind: OftenKind | null = null
    const regularField = field(`Then each time ${currencySymbol()}`, enquiryParts.regular)
    if (enquiry) {
      enquiryParts.regular.value = customer.reg_price_pence == null ? '' : (customer.reg_price_pence / 100).toFixed(2)
      for (const o of cycleOptions().filter((x) => !x.marker)) enquiryParts.every.append(el('option', { value: o.days === null ? '' : String(o.days) }, o.days === null ? 'Not set' : o.label))
      enquiryParts.every.value = customer.cycle_days ? String(customer.cycle_days) : ''
      // An Every the list does not have (an enquiry's own odd number of weeks) stays what it is.
      if (customer.cycle_days && enquiryParts.every.value !== String(customer.cycle_days)) {
        enquiryParts.every.append(el('option', { value: String(customer.cycle_days) }, cycleLabel(customer.cycle_days, null)))
        enquiryParts.every.value = String(customer.cycle_days)
      }
      enquiryParts.postcode.value = customer.postcode ?? ''
    }
    // Insides as well (0070, Liam 13 Oct 2026): the price box is then the outside price, the insides have their own,
    // and the visit saves the two together as its price with the insides part kept. The customer's inside price is the
    // starting figure. Book a visit and Edit visit only; Log completed work keeps its one price box (the whole price).
    const insidesOn = el('input', { type: 'checkbox' }) as HTMLInputElement
    const inside = el('input', { inputmode: 'decimal', placeholder: 'Inside price' }) as HTMLInputElement
    // Book them in from a quote (0092, Write a quote B): its insides and first-visit extras fill themselves in, to change.
    let wholeQuote = false
    try { wholeQuote = JSON.parse((enquiry ?? options.linkLead)?.quote_json ?? '{}').whole_breakdown === true } catch { /* Older ordinary quote. */ }
    const quoted = quotedOf((enquiry ?? options.linkLead)?.quote_json)
    const insidesBefore = clean
      ? options.addOfferToVisit && options.acceptedOffer?.offer.insides ? options.acceptedOffer.offer.insides.pence : hasInsides(clean) ? clean.inside_pence as number : null
      : options.acceptedOffer?.offer.insides?.pence ?? quoted?.insides?.pence ?? null
    insidesOn.checked = newLook && insidesBefore !== null
    // Extras for this visit (0087, design B: folded on most visits, opened by itself when the visit has some).
    const extras = extrasBox(clean ? [...extrasOf(clean.extras_json), ...(options.addOfferToVisit ? options.acceptedOffer?.offer.items ?? [] : [])] : options.draft?.extras ?? [...(quoted?.extras ?? []), ...(options.acceptedOffer?.offer.items ?? [])], () => { repaintBook?.() })
    // The Book button's whole follows the price and the insides too, not only the extras (GPT-6.1's extras review 12).
    for (const box of [price, inside]) box.addEventListener('input', () => { if (extras.total() > 0) repaintBook?.() })
    insidesOn.addEventListener('change', () => { if (extras.total() > 0) repaintBook?.() })
    if (insidesOn.checked) inside.value = ((insidesBefore as number) / 100).toFixed(2)
    // Regular price on the cycle; the ad-hoc price when there is no cycle or
    // the visit is more than half a cycle late (src/pricing.ts). The hint
    // says which and why; one tap swaps to the other.
    // Job 1's first-clean ("ad-hoc") price and its "missed" rule read job 1's visits only (PLAN 2d); an extra job
    // has its one price.
    const lastDone = (pick ? jobVisits(jobOneOf(customer)) : cleans).filter(c => c.state === 'done').map(c => c.date).sort().at(-1) ?? null
    // Book them in: their first clean is the quote's first-clean price (else its regular one), whatever the day.
    const useQuote = el('input', { type: 'checkbox', 'aria-label': 'Use quote' }) as HTMLInputElement
    useQuote.checked = false
    const canChooseQuote = (): boolean => !!customerQuote && !options.customerOffer && !enquiry && !options.linkLead && !clean && !done && !newJob && (!job || job.id === null)
    const suggest = (): ReturnType<typeof priceFor> => (bookingQuote && (enquiry || options.linkLead) && !clean) || (canChooseQuote() && useQuote.checked)
      ? { pence: (bookingQuote && (enquiry || options.linkLead) ? bookingQuote : customerQuote)!.total_pence, why: 'adhoc', lateDays: null }
      : options.customerOffer
      ? {pence: 0, why: 'regular', lateDays: null} : enquiry
      ? { pence: customer.first_clean_price_pence ?? customer.reg_price_pence ?? null, why: customer.first_clean_price_pence != null ? 'adhoc' : customer.reg_price_pence != null ? 'regular' : 'none', lateDays: null }
      : job && job.id !== null
      ? { pence: job.price_pence, why: job.price_pence === null ? 'none' : 'regular', lateDays: null }
      : priceFor(customer, date.value || todayISO(), lastDone)
    let priceEdited = false
    price.addEventListener('input', () => { priceEdited = true })
    const initialPrice = options.customerOffer ? 0 : clean ? (newLook ? outsidePence(clean) : clean.amount_pence) : suggest().pence
    price.value = initialPrice === null ? '' : (initialPrice / 100).toFixed(2)
    const priceHint = el('p', { class: 'muted field-help' })
    const swapTo = el('button', { type: 'button', class: 'link-button price-swap' })
    // The new visit's big price and its chips follow the price box, whatever changed it (fresh only; set below).
    let paintPrice = (): void => {}
    // The insides line under the price (Book a visit and Edit visit; set with the price section).
    let paintInsides = (): void => {}
    const explainPrice = (): void => {
      explainPriceWords()
      paintPrice()
    }
    const explainPriceWords = (): void => {
      if (clean) { priceHint.textContent = 'Price recorded for this visit.'; swapTo.hidden = true; return }
      if (options.customerOffer) { priceHint.textContent = 'Accepted extras only. A separate one-off job, at the offered prices. Their windows stay as they are.'; swapTo.hidden = true; return }
      if (bookingQuote && (enquiry || options.linkLead) || canChooseQuote() && useQuote.checked) { priceHint.textContent = `From quote ${quoteLabel(bookingQuote && (enquiry || options.linkLead) ? bookingQuote : customerQuote!)}. Change it to what you agreed.`; swapTo.hidden = true; return }
      if (enquiry) { priceHint.textContent = suggest().pence === null ? 'No price on the enquiry yet. Type what you agreed.' : 'From their quote. Change it to what you agreed.'; swapTo.hidden = true; return }
      if (job && job.id !== null) { priceHint.textContent = job.price_pence === null ? `No price on file for ${(job.name ?? '').toLowerCase() || 'this job'}. Enter the agreed price, or leave it unpriced.` : `The ${(job.name ?? '').toLowerCase() || 'job'} price. Adjust for the work agreed.`; swapTo.hidden = true; return }
      if (newJob) { priceHint.textContent = 'Enter the agreed price for this job, or leave it unpriced.'; swapTo.hidden = true; return }
      if (kind.value !== 'window') { priceHint.textContent = 'Enter the agreed service price, or leave blank as unpriced.'; swapTo.hidden = true; return }
      const s = suggest()
      const other = s.why === 'regular' ? customer.first_clean_price_pence : customer.reg_price_pence
      if (s.why === 'none') priceHint.textContent = 'No regular price on file. Enter an agreed price, or leave it unpriced.'
      else if (s.why === 'adhoc') priceHint.textContent = 'Ad-hoc price: no regular cycle agreed.'
      else if (s.why === 'missed') priceHint.textContent = `Ad-hoc price: this visit is ${Math.round((s.lateDays ?? 0) / 7) === 1 ? '1 week' : `${Math.round((s.lateDays ?? 0) / 7)} weeks`} past their cycle.`
      else priceHint.textContent = 'Regular price, on their cycle. Adjust for the work agreed.'
      swapTo.hidden = other === null || other === undefined || other === s.pence
      swapTo.textContent = other === null || other === undefined ? '' : `Use ${s.why === 'regular' ? 'ad-hoc' : 'regular'} price ${money(other)} instead`
    }
    swapTo.addEventListener('click', () => {
      priceEdited = true
      const s = suggest()
      const other = s.why === 'regular' ? customer.first_clean_price_pence : customer.reg_price_pence
      if (other !== null && other !== undefined) price.value = (other / 100).toFixed(2)
      priceHint.textContent = `${s.why === 'regular' ? 'Ad-hoc' : 'Regular'} price, chosen by hand.`
      swapTo.hidden = true
      paintPrice()
    })
    const quoteChoice = customerQuote ? el('label', { class: 'check-label booking-quote-choice' }, useQuote, `Use quote ${quoteLabel(customerQuote)} ${money(customerQuote.total_pence)}?`) : null
    const syncQuoteChoice = (): void => {
      if (quoteChoice) quoteChoice.hidden = !canChooseQuote()
      if (!canChooseQuote()) useQuote.checked = false
    }
    useQuote.addEventListener('change', () => {
      priceEdited = true
      const p = suggest().pence
      price.value = p == null ? '' : (p / 100).toFixed(2)
      explainPrice()
    })
    explainPrice()
    kind.addEventListener('change', () => {
      priceEdited = false
      // Back to the visit's own kind: its own price again, the outside part of it where the box is the outside price (0070).
      const p = kind.value === clean?.kind ? (newLook ? outsidePence(clean) : clean.amount_pence) : kind.value === 'window' ? suggest().pence : null
      price.value = p == null ? '' : (p / 100).toFixed(2)
      explainPrice()
    })
    date.addEventListener('change', () => { if (!priceEdited && !clean && !newJob && (kind.value === 'window' || (job && job.id !== null))) { const p = suggest().pence; price.value = p == null ? '' : (p / 100).toFixed(2); explainPrice() } })
    // The Job select (several jobs on, a new visit): the customer's running jobs, then a new job of each kind. It
    // takes the place of the Service select; choosing a job brings its own day and price.
    const jobSelect = el('select', { 'aria-label': 'Job' }) as HTMLSelectElement
    const newJobBox = el('div', { class: 'new-job-box' })
    const newJobName = el('input', { maxlength: '100' }) as HTMLInputElement
    // How often for a new job, the one question (src/how-often.ts; Liam, 27 Oct 2026: "The book a visit small job next"):
    // Regular and its weeks, or Just once (a one-off, as before when nothing was ticked). No Ad hoc for a job.
    let newJobOften: { days: number | null; kind: OftenKind | null } = { days: null, kind: 'once' }
    const oftenSlot = el('div', { class: 'how-often' })
    const syncRegular = (): void => {
      newJobOften = { days: null, kind: 'once' }
      clear(oftenSlot)
      oftenSlot.append(howOftenPicker({ value: { days: null, marker: 'Just once' }, kinds: ['regular', 'once'], question: 'How often?', onChange: (v, k) => { newJobOften = { days: v?.days ?? null, kind: k } } }).node)
    }
    newJobBox.append(
      el('label', { class: 'booking-field' }, el('span', {}, 'Name of the job'), newJobName),
      oftenSlot,
      el('p', { class: 'muted field-help' }, 'A regular job has its own price and how often, and Roundly books the next one when this is done. Just once books nothing after it.'),
    )
    syncRegular()
    if (pick && !clean && !options.customerOffer) {
      for (const j of pick.jobs.filter((x) => !x.stopped_on)) jobSelect.append(el('option', { value: j.id === null ? 'main' : String(j.id) }, `${j.name?.trim() || kindLabel(j.id === null ? 'window' : j.kind)}${j.price_pence !== null ? ` · ${money(j.price_pence)}` : ''}${everyWords(j.cycle_days) ? ` · ${everyWords(j.cycle_days)}` : ''}`))
      for (const [k, label] of NEW_JOB_KINDS) jobSelect.append(el('option', { value: `new:${k}` }, `New job: ${k === 'other' ? label : kindLabel(k)}`))
      jobSelect.value = job ? (job.id === null ? 'main' : String(job.id)) : 'new:gutter'
      if (!job || job.stopped_on) jobSelect.value = jobSelect.options[0]?.value ?? 'new:gutter'
      const applyJob = (initial = false): void => {
        priceEdited = false
        const v = jobSelect.value
        if (v.startsWith('new:')) {
          const k = v.slice(4) as JobKind
          newJob = { kind: k }
          job = null
          kind.value = k
          newJobName.value = k === 'other' ? '' : kindLabel(k)
          syncRegular()
          newJobBox.hidden = false
          price.value = ''
        } else {
          newJob = null
          job = pick.jobs.find((j) => (j.id === null ? 'main' : String(j.id)) === v) ?? pick.jobs[0]!
          kind.value = job.id === null ? 'window' : job.kind ?? 'other'
          newJobBox.hidden = true
          date.value = job.id === null ? nextPlanningDate({ ...customer, last_clean: last }) : jobDate(job)
          const p = suggest().pence
          price.value = p == null ? '' : (p / 100).toFixed(2)
        }
        syncQuoteChoice(); explainPrice()
        if (!initial) { confirmed.checked = false; timeConfirmed.checked = false; refreshTimeAgreement() }
        // The day's workload below is drawn once the form is built; only a later change redraws it here.
        if (!initial) void preview()
      }
      jobSelect.addEventListener('change', () => applyJob())
      if (jobSelect.value.startsWith('new:')) applyJob(true)
      else {
        newJobBox.hidden = true
        if (job) kind.value = job.id === null ? 'window' : job.kind ?? 'other'
      }
    }
    const note = el('textarea', { rows: '3', maxlength: '2000' })
    const request = options.initialVisitNote?.trim()
    note.value = [clean?.note ?? '', request && clean?.note !== request && !(clean?.note ?? '').endsWith('\n' + request) ? request : ''].filter(Boolean).join('\n')
    const confirmed = el('input', { type: 'checkbox' })
    confirmed.checked = !!options.agreedDate || clean?.confirmed === 1
    const confirmation = el('label', { class: 'check-label' }, confirmed, el('span', {}, 'Day agreed'))
    const timeConfirmed = el('input', { type: 'checkbox' })
    timeConfirmed.checked = !!clean && date.value === clean.date && timeIsAgreed(clean)
    const timeConfirmation = el('label', { class: 'check-label' }, timeConfirmed, el('span', {}, 'Time agreed'))
    // A round that gives no set times (Your business) asks for the day only: no time box, no Time agreed. A time saved
    // before the switch rides along unseen, so it is still there if the round switches back.
    const timesOn = setTimesOn()
    const agreementHint = el('p', { class: 'muted field-help' }, timesOn ? 'Tick each once the customer has agreed it. Changing the time clears its tick; changing the day clears both.' : 'Tick it once the customer has agreed the day. Changing the day clears it.')
    const refreshTimeAgreement = (): void => {
      timeConfirmed.disabled = !confirmed.checked || !time.value.trim()
      if (timeConfirmed.disabled) timeConfirmed.checked = false
    }
    const resetAgreement = (): void => { confirmed.checked = false; timeConfirmed.checked = false; refreshTimeAgreement(); void preview() }
    date.addEventListener('change', resetAgreement)
    time.addEventListener('change', () => { timeConfirmed.checked = false; refreshTimeAgreement(); void preview() })
    time.addEventListener('input', () => { timeConfirmed.checked = false; refreshTimeAgreement() })
    confirmed.addEventListener('change', refreshTimeAgreement)
    refreshTimeAgreement()
    // Arranged with them already (Book them in): the day picked is the day agreed, the time set the time agreed. Run after
    // the clearing above, so they follow what is picked, never the day only suggested. A switch turned off by hand stays
    // off whatever is picked after (Sol, 19 Oct 2026, asked that a change turn them off; correcting a wrong tap while
    // putting in the arrangement would then book it not agreed without a word, so the owner's own Off is what holds).
    let dayHeld = false, timeHeld = false
    confirmed.addEventListener('change', () => { dayHeld = !confirmed.checked })
    timeConfirmed.addEventListener('change', () => { timeHeld = !timeConfirmed.checked })
    const arrangeDay = (): void => {
      if (!options.arranged || dayHeld || weekOnly.checked || !date.value) return
      confirmed.checked = true
      refreshTimeAgreement()
      if (time.value.trim() && !timeHeld && !timeConfirmed.disabled) timeConfirmed.checked = true
    }
    const arrangeTime = (): void => {
      if (!options.arranged || timeHeld || !confirmed.checked || !time.value.trim()) return
      refreshTimeAgreement()
      if (!timeConfirmed.disabled) timeConfirmed.checked = true
    }
    if (options.arranged) {
      date.addEventListener('change', arrangeDay)
      time.addEventListener('change', arrangeTime)
      time.addEventListener('input', arrangeTime)
    }
    // 754 imported cleans are paid with no method on file. Without a "paid,
    // method unknown" answer, opening one of them preselected "Paid bank" and
    // saving invented a bank transfer that nobody had recorded. The buttons
    // (src/payment-pills.ts) keep that answer for the visits that carry it.
    // On a round with brought money, Cash asks what was taken here too (Astra r2, finding 12), starting on what is due.
    const moneyRound = getMe()?.broughtMoney === true
    const alreadyCash = clean?.state === 'done' && clean.payment_status === 'paid' && clean.payment_method === 'cash'
    const startTaken = clean ? (alreadyCash && (clean.cash_taken_pence ?? 0) > 0 ? clean.cash_taken_pence! : moneyPending(clean) ? null : toCollect(clean)) : null
    const payment = paymentPills(clean?.state === 'done' ? (clean.payment_status === 'paid' ? clean.payment_method ?? 'paid' : clean.payment_status) : null, moneyRound ? { askCash: true, cashPence: startTaken } : {})
    // Several jobs: a new visit picks its job; an existing one says which job it is (a visit never changes job).
    const serviceField = pick && !clean && !options.customerOffer
      ? field('Job', jobSelect)
      : pick && clean && job && job.id !== null
        ? el('p', { class: 'booking-field booking-job-name' }, el('span', {}, 'Job'), el('strong', {}, job.name?.trim() || kindLabel(job.kind)))
        : field('Service', kind)
    if (!newLook) {
      fields.append(field('Date', date), ...(timesOn ? [field('Time (optional)', time)] : []))
      if (!done) fields.append(confirmation, ...(timesOn ? [timeConfirmation] : []), agreementHint)
      fields.append(...(askWho ? [field('Worker (optional)', worker), workers] : []),
        field('Duration in minutes (optional)', duration), serviceField, field(`Price ${currencySymbol()} (optional)`, price), priceHint, swapTo)
      // A new job's name and "make it a regular job" sit straight under the Job choice that opened them.
      if (pick && !clean) fields.append(newJobBox)
      fields.append(field('Visit note', note))
      if (done) fields.append(payment.node)
    }
    const previewBox = el('section', { class: 'booking-preview', 'aria-label': 'Selected day workload', 'aria-live': 'polite' })
    let previewRows: Clean[] = []
    let previewSequence = 0
    const overlap = el('p', { class: 'overlap-warning', role: 'status' })
    // Their parking hours (Parking notes, 0084): said under the time, the button reading "anyway" (parking-ask.ts).
    const parkingLine = el('p', { class: 'overlap-warning parking-warning', role: 'status' })
    let parkingAnyway = false
    let repaintBook: (() => void) | null = null
    // "Put it in that week" (2026-09-18). A visit can hold a WEEK with no day:
    // it sits in the diary's week panel under "No day yet" and on no day card,
    // so nothing pretends the work is scheduled and the day's money, workload
    // and map leave it out. Liam wanted Janet in the week of the 28th after she
    // asked to skip one, with no day pinned, so he could message her about that
    // week and set the day from the reply.
    //
    // New bookings only: turning a visit that already HAS a day back into a
    // week is a different save (the server stamps 'manual' the moment a human
    // touches the date) and nobody has asked for it. Never on completed work —
    // work that happened, happened on a day.
    const weekOnly = el('input', { type: 'checkbox' })
    const weekOnlyHint = el('p', { class: 'muted field-help' }, '')
    const weekOnlyLabel = el('label', { class: 'check-label' }, weekOnly, el('span', {}, 'Put it in that week, I\'ll pick the day later'))
    // The new visit's own parts that go with a day (its time, the switches, the day's line) follow too (set below).
    let afterWeekOnly = (): void => {}
    const applyWeekOnly = (): void => {
      const on = weekOnly.checked
      // A day nobody has chosen carries no time, and cannot have been agreed:
      // both controls go quiet rather than sitting there lying. The day
      // workload below is about a day too, so it goes with them.
      if (on) { time.value = ''; confirmed.checked = false; timeConfirmed.checked = false }
      time.disabled = on
      confirmed.disabled = on
      refreshTimeAgreement()
      agreementHint.hidden = on
      previewBox.hidden = on
      overlap.hidden = on
      // Where Due is (tidy-up, 20 Oct 2026): the bar at the foot of the diary on a phone, the column down its left on a computer.
      const due = typeof matchMedia === 'function' && matchMedia('(max-width: 760px)').matches ? 'in Due at the foot of the diary' : 'in Due down the left of the diary'
      weekOnlyHint.textContent = on && date.value && date.validity.valid
        ? `Waits ${due} for the week of ${shortDate(mondayOf(date.value))}, on no day until you put it on one.`
        : `Waits ${due} instead of landing on a day.`
      afterWeekOnly()
    }
    if (!done) {
      weekOnly.addEventListener('change', applyWeekOnly)
      date.addEventListener('change', applyWeekOnly)
      if (!clean && !options.replyConfirmation) { if (!fresh) fields.append(weekOnlyLabel, weekOnlyHint); applyWeekOnly() }
    }
    const checkOverlap = (): void => {
      if (!timesOn) { overlap.textContent = ''; return }
      const names = overlapNames(previewRows, date.value, time.value, duration.value ? Number(duration.value) : null, worker.value, clean?.id)
      overlap.textContent = names.length ? `Time overlaps with ${names.join(', ')} for ${worker.value}. Check before saving.` : ''
      checkParking()
    }
    function checkParking(): void {
      const who: ParkingCustomer = { customer_name: customer.name, parking: customer.parking_restrictions ?? null, duration_minutes: duration.value ? Number(duration.value) : null }
      const hours = timesOn ? parkingClash(who, time.value.trim() || null, date.value || null) : null
      parkingAnyway = !!hours
      parkingLine.textContent = hours ? parkingWarning(who, hours) : ''
      repaintBook?.()
    }
    time.addEventListener('input', checkParking)
    time.addEventListener('change', checkParking)
    duration.addEventListener('change', checkParking)
    checkParking()
    // A new visit reads the days once for its day buttons and draws the chosen day from them (set below).
    let paintDays: (() => Promise<void>) | null = null
    async function preview(): Promise<void> {
      if (paintDays) return paintDays()
      const sequence = ++previewSequence
      previewRows = []
      clear(previewBox); overlap.textContent = ''
      if (!date.value || !date.validity.valid) return
      previewBox.textContent = 'Loading this day…'
      try {
        const data = await api<SchedulePayload>(`/api/schedule?from=${date.value}&to=${date.value}`)
        if (closed || sequence !== previewSequence || !previewBox.isConnected) return
        previewRows = data.cleans.filter(c => c.id !== clean?.id && c.state !== 'cancelled')
        clear(previewBox)
        const visits = previewRows.filter(c => !isContact(c))
        const committed = visits.filter(c => !isSuggestion(c))
        const suggestions = visits.filter(isSuggestion)
        const valueSummary = (rows: Clean[]): string => {
          const missing = rows.filter(c => c.amount_pence === null).length
          return `${money(rows.reduce((sum, c) => sum + (c.amount_pence ?? 0), 0))}${missing ? ` · ${missing} price${missing === 1 ? '' : 's'} missing` : ''}`
        }
        previewBox.append(el('h3', {}, dayLabel(data.from)), el('p', {}, `${workload(committed)} · ${valueSummary(committed)} booked / done`))
        if (suggestions.length) previewBox.append(el('p', { class: 'muted' }, `Suggestions: ${workload(suggestions)} · ${valueSummary(suggestions)} potential`))
        previewBox.append(el('p', { class: 'muted' }, timesOn ? 'Excludes this visit. Unknown times and travel are not included in overlap checks.' : 'Excludes this visit.'))
        // In the Diary's one order for the day (tidy-up, Liam, 20 Oct 2026), Roundly's suggestions after.
        for (const c of [...diaryDayOrder(previewRows.filter((v) => !isSuggestion(v)), severalJobsOn()), ...previewRows.filter(isSuggestion)]) {
          previewBox.append(el('div', { class: 'preview-job' }, el('span', {}, `${setTimesOn() ? `${shownVisitTime(c) ?? 'no set time'} · ` : ''}${c.customer_name}${visitName(c, 'Unassigned')} · ${money(c.amount_pence)}`), el('span', { class: 'badge' }, visitWords(c, diaryCustomer(c)).text)))
        }
        if (!visits.length) previewBox.append(el('p', {}, 'No other visits on this day.'))
        clear(workers)
        for (const w of [...new Set(data.workers ?? [])]) workers.append(el('option', { value: w }))
        checkOverlap()
      } catch (err) {
        if (sequence !== previewSequence || closed) return
        previewBox.textContent = `Workload unavailable: ${(err as Error).message}. Availability has not been checked.`
      }
    }
    duration.addEventListener('input', checkOverlap); worker.addEventListener('input', checkOverlap)
    const error = el('p', { class: 'form-error', role: 'alert' })
    const save = el('button', { type: 'submit', class: 'btn-primary' }, done ? 'Log completed work' : options.replyConfirmation ? 'Save visit & review message' : clean ? 'Save visit' : 'Book visit')
    const cancel = el('button', { type: 'button' }, 'Cancel')
    cancel.addEventListener('click', dismiss)
    const actions = el('footer', { class: 'booking-footer' }, error, cancel, save)
    let focusStart = (): void => date.focus()
    let freshParts: { days: HTMLElement; who: HTMLElement; more: HTMLDetailsElement } | null = null
    if (newLook) freshParts = layoutNew()
    form.append(fields, ...(newLook ? [] : [previewBox, overlap, parkingLine]), actions)
    content.append(form)
    void preview()
    // Book them in: what was picked and typed before "Already a customer?", put back as if picked again (a day gone
    // since is left out). An existing customer keeps their own prices.
    const draft = options.draft
    if (draft && fresh) {
      if (draft.date && draft.date >= todayISO() && draft.date !== date.value) { date.value = draft.date; date.dispatchEvent(new Event('change')) }
      else if (draft.date && draft.date === date.value) arrangeDay()
      if (draft.time && timesOn) { time.value = draft.time; time.dispatchEvent(new Event('input')); time.dispatchEvent(new Event('change')) }
      if (draft.worker) { worker.value = draft.worker; worker.dispatchEvent(new Event('change')) }
      if (draft.duration) { duration.value = draft.duration; duration.dispatchEvent(new Event('change')) }
      if (draft.note) note.value = draft.note
      if (enquiry) {
        if (draft.first !== undefined) { price.value = draft.first; price.dispatchEvent(new Event('input')) }
        if (draft.regular !== undefined) enquiryParts.regular.value = draft.regular
        // ...and How often's buttons with it (Sonnet's extras review 6).
        if (draft.every !== undefined) { enquiryParts.every.value = draft.every; enquiryParts.every.dispatchEvent(new Event('change')) }
        if (draft.postcode !== undefined) enquiryParts.postcode.value = draft.postcode
      }
    }
    // The first-booking guide, for a new visit or one put in a week with no day yet: the day, then who does
    // it, then save.
    const guiding = !!options.guide && !done && (!clean || clean.source === 'week')
    if (guiding) {
      const dayField = freshParts?.days ?? date.closest<HTMLElement>('.booking-field')
      const whoField = freshParts?.who ?? worker.closest<HTMLElement>('.booking-field')
      // A one-person round has no who step (Liam, 19 Oct 2026): the day, then save, "of 3".
      const steps = alone ? 3 : 4
      const toSave = (): void => guideTo(`${steps} of ${steps}: Tap ${save.textContent} to put it in the diary.`, save, undefined, true)
      // Blank means whoever the diary has on that day (functions/lib/worker-day.ts), not always the owner (Codex).
      const toWho = (): void => {
        if (alone) { toSave(); return }
        if (freshParts) freshParts.more.open = true
        if (whoField) guideTo(freshParts ? "3 of 4: Who's doing it? Tap a name, or leave it on whoever is out that day." : "3 of 4: Who's doing it? Type a name, or leave it blank for whoever is on that day.", whoField, toSave, true)
        else toSave()
      }
      guideTo(clean?.source === 'week' ? `2 of ${steps}: Pick the day in the week of ${shortDate(date.value)}.` : `2 of ${steps}: Pick the day. Roundly suggests ${shortDate(date.value)}.`, dayField, toWho)
      date.addEventListener('change', toWho, { once: true })
      if (!alone) worker.addEventListener('change', toSave, { once: true })
    }
    form.addEventListener('submit', async e => {
      e.preventDefault()
      if (busy) return
      if (!sameIdentity()) { error.textContent = 'Your sign-in changed. Close this editor and reopen the visit.'; return }
      error.textContent = ''
      // A visit never lands on a day that has gone, new or moved (a week held from today on may still start on a Monday
      // gone by); a visit left on the day it is saved on can still be changed.
      if (newLook && date.value && date.value < todayISO() && date.value !== clean?.date) { error.textContent = GONE_DAY_WORDS; return }
      let pence: number | null
      try { pence = parseMoney(price.value) } catch (err) { error.textContent = (err as Error).message; price.focus(); return }
      // Insides as well: the visit's price is the two together, the insides part kept beside it (0070).
      let insidePence: number | null = null
      if (newLook && insidesOn.checked) {
        try { insidePence = parseMoney(inside.value) } catch (err) { error.textContent = (err as Error).message; inside.focus(); return }
        if (insidePence === null) { error.textContent = 'Put in the inside price, for example 40, or turn Insides too off.'; inside.focus(); return }
        if (pence === null) { error.textContent = 'Put in the outside price as well as the inside price.'; price.focus(); return }
        pence += insidePence
      }
      // Extras for this visit (0087): each named and priced, added to the visit's price (never the customer's).
      const extrasList = newLook ? extras.value() : []
      if (newLook) {
        const problem = extras.problem()
        if (problem) { error.textContent = problem; return }
      }
      const extrasPence = extrasTotal(extrasList)
      if (extrasList.length && pence === null && !enquiry) { error.textContent = "Put in the visit's own price too, 0 if it's only the extras."; price.focus(); return }
      const path = enquiry ? `/api/leads/${enquiry.id}/book` : clean ? `/api/cleans/${clean.id}` : `/api/customers/${customer.id}/cleans`
      // Anything unresolved on this row blocks, not just work still moving.
      // A parked conflict, a rejection or an expired sign-in also holds this
      // save behind it inside the queue (see api.ts) — the form used to let it
      // through and then say "waiting for signal", which sent Lewis looking
      // for coverage for a change that was never going to send until he dealt
      // with the earlier one.
      const blocked = getQueue().find(q => q.path === path)
      if (blocked) {
        error.textContent = blocked.status === 'sending' || blocked.status === 'queued'
          ? enquiry ? `Booking ${first || customer.name} in is still waiting to save. It goes as soon as it can.` : 'A change for this visit is still waiting to save. Check the saving indicator before adding another.'
          : blocked.status === 'signed-out'
            ? 'An earlier change to this visit is held until you sign in again. Deal with it in the saving indicator at the bottom of the screen first.'
            : `An earlier change to this visit needs your attention before this one can save (${blocked.status === 'conflict' ? 'it was edited elsewhere' : blocked.error ?? 'it was rejected'}). Open the saving indicator at the bottom of the screen.`
        return
      }
      if (done && !payment.value) {
        error.textContent = 'Pick how it was paid: Cash, Bank or Not paid yet.'
        flagMissingPayment(payment)
        return
      }
      // Cash on a round with brought money: what was taken, never guessed from the price (Astra r3, finding 10).
      if (done && moneyRound && payment.value === 'cash') {
        const typed = payment.cashPence()
        if (typed === null || Number.isNaN(typed)) {
          error.textContent = typed === null ? 'Enter how much cash was taken (0 if nothing).' : `Enter the cash in ${currencyWord()}, for example 24 or 24.50.`
          flagMissingPayment(payment)
          return
        }
      }
      // Several jobs: gutters never go after the windows (Liam Q11). A gutters day up to a fortnight after a booked
      // windows visit is refused, with the windows day one tap away. Work already done is not booking a day.
      const guttersKind = options.customerOffer && options.acceptedOffer?.offer.items.some(e => e.kind === 'gutters') ? 'gutter' : pick && !done ? (newJob ? newJob.kind : clean ? (job && job.id !== null ? job.kind : clean.kind) : job?.id !== null ? job?.kind : kind.value) : null
      const windowsDay = guttersKind === 'gutter' && date.value ? windowsDayBefore(date.value, cleans.filter((c) => c.id !== clean?.id), todayISO()) : null
      if (windowsDay) {
        error.textContent = `The windows are booked ${dayLabel(windowsDay)}. Gutters go on the windows day or the day before, never after.`
        const use = el('button', { type: 'button', class: 'link-button' }, `Use ${dayLabel(windowsDay)}`)
        // The new day is a different arrangement: agreement goes, and the date's own handling runs (price, week,
        // the day's workload), exactly as if the owner had picked it (Astra r2, finding 4).
        use.addEventListener('click', () => { date.value = windowsDay; error.textContent = ''; date.dispatchEvent(new Event('change')) })
        error.append(' ', use)
        return
      }
      busy = true; save.disabled = true; fields.disabled = true; cancel.disabled = true; close.disabled = true
      // Book them in: the enquiry, the customer and the visit in one save, or nothing.
      if (enquiry) {
        const unlock = (): void => { busy = false; save.disabled = false; fields.disabled = false; cancel.disabled = false; close.disabled = false }
        let regularPence: number | null
        try { regularPence = parseMoney(enquiryParts.regular.value) } catch (err) { unlock(); error.textContent = (err as Error).message; enquiryParts.regular.focus(); return }
        if (enquiryKind === 'regular' && !enquiryParts.every.value) { unlock(); error.textContent = 'Pick every how many weeks, or another kind of customer.'; return }
        // Just once or Ad hoc: one price, saved as both, so neither old figure wins later (pricing.ts).
        if (enquiryMarker && !wholeQuote && !(enquiryKind === 'adhoc' && enquiry && regularChoiceOn(enquiry)?.choice === 'once')) regularPence = pence
        const body = {
          mode: 'create', version: enquiry.version, other_jobs:otherJobs?.value()??null, ...(options.offersDeferred ? { defer_offers: true } : {}), ...(options.acceptedOffer ? { extra_offer: options.acceptedOffer.choice } : {}), postcode: enquiryParts.postcode.value.trim() || null,
          // The first clean alone; the insides ride on the visit as its own part (the server adds them to its price).
          first_pence: pence !== null && insidePence ? pence - insidePence : pence, regular_pence: regularPence, cycle_days: enquiryParts.every.value ? Number(enquiryParts.every.value) : null,
          ...(enquiryMarker ? { cycle_text: enquiryMarker } : {}),
          visit: {
            date: date.value, time_slot: time.value.trim() || null, ...nameSent(worker.value.trim() || null),
            duration_minutes: duration.value ? Number(duration.value) : null, kind: kind.value, note: note.value.trim() || null,
            confirmed: confirmed.checked ? 1 : 0, time_agreed_for: confirmed.checked && timeConfirmed.checked ? agreedTimeKey(date.value, time.value) : null,
            // The first clean stays first_pence; the server adds the extras to this visit only (functions/lib/book-lead.ts).
            ...(extrasList.length ? { extras_json: extrasList } : {}),
            ...(insidePence ? { inside_pence: insidePence } : {}),
          },
        }
        let queuedKey: string | null = null
        const ack = await mutate({ label: `Book in: ${customer.name} ${date.value}`, method: 'POST', path, body, onQueued: (m) => { queuedKey = m.key } })
        busy = false; cancel.disabled = false; close.disabled = false
        window.dispatchEvent(new CustomEvent('dropby:data-changed'))
        if (ack.ok) {
          // Booked for the account that tapped Book; signed in as another since, nothing of it is shown here.
          if (!sameIdentity()) { dialog.close(); return }
          renderEnquiryBooked(enquiry, customer, ack)
        } else if (ack.status >= 400 && ack.status < 500) {
          fields.disabled = false; save.disabled = ack.status === 409
          error.textContent = ack.status === 409 ? `${ack.error ?? 'It changed somewhere else first.'} Your choices are still here: close this and open their card again.` : ack.error ?? 'Check the boxes and try again.'
        } else if (ack.signedOut) {
          error.textContent = `Your sign-in has expired. ${first || customer.name}'s booking is held, not lost: sign in again and it saves itself. You can close this.`
          cancel.textContent = 'Close'
        } else {
          error.textContent = `Waiting for signal. ${first || customer.name} will be booked in as soon as it sends. You can close this.`
          cancel.textContent = 'Close'
          if (queuedKey) watchLanding(enquiry, customer, queuedKey, body.visit.date, error)
        }
        return
      }
      const body: Record<string, unknown> = {
        date: date.value, time_slot: time.value.trim() || null, ...nameSent(worker.value.trim() || null),
        duration_minutes: duration.value ? Number(duration.value) : null,
        kind: kind.value, amount_pence: pence === null ? null : pence + extrasPence, note: note.value.trim() || null,
      }
      // Sent only from the form that shows the switch, so Log completed work never clears insides it doesn't show.
      if (newLook) body.inside_pence = insidePence
      // ...and the extras the same way: Log completed work keeps the ones the visit has.
      if (newLook) body.extras_json = extrasList.length ? extrasList : null
      if (canChooseQuote() && useQuote.checked) body.use_quote_id = customerQuote!.id
      if (clean) body.version = clean.version
      // Several jobs: a new visit for an extra job names it; a new job is made first, then booked.
      if (!clean && pick && job && job.id !== null) {
        body.job_id = job.id
        body.kind = job.kind ?? kind.value
      }
      if (done) {
        body.state = 'done'
        // 'paid' is money in with no method recorded — a real answer, and the
        // one 754 imported cleans give. Only cash and bank name a method.
        const method = ['cash', 'bank'].includes(payment.value) ? payment.value : null
        body.payment_status = method || payment.value === 'paid' ? 'paid' : payment.value
        body.payment_method = method
        if (moneyRound && method) {
          body.paid_on = todayISO()
          if (method === 'cash') {
            const cash = payment.cashPence()
            // Checked above: a figure was typed. Sent when it says something new: a first payment, or a figure that
            // differs from the cash on file.
            if (cash !== null && !Number.isNaN(cash) && (!alreadyCash || cash !== startTaken)) body.taken_pence = cash
          }
        }
      } else {
        body.confirmed = confirmed.checked ? 1 : 0
        body.time_agreed_for = confirmed.checked && timeConfirmed.checked ? agreedTimeKey(date.value, time.value) : null
        // A suggested date stays a suggestion until the customer has agreed
        // it (Liam 2026-09-11): only a confirmed save adopts it as a firm booking.
        if (clean) {
          if (confirmed.checked) body.adopt_booking = true
          // The first-booking guide on a visit put in a week with no day: the day on the form is now the chosen
          // day, even when it is the Monday the week already showed.
          if (options.guide && clean.source === 'week') body.adopt_booking = true
        } else {
          // Only when ADDING a visit: a change to one that exists never sends
          // it, or the save refuses the whole change ("unknown field:
          // allow_additional"; found 28 Sep 2026 when "Change price, worker
          // or note" on a visit could not save).
          body.allow_additional = additional
        }
        // A week with no day still has to carry a date, so it carries the
        // Monday: one date per week, so two visits put in the same week read
        // as the same week. No time and no agreement can survive it — there is
        // no day for either to be about.
        if (!clean && weekOnly.checked) {
          body.date = mondayOf(date.value)
          body.time_slot = null
          body.confirmed = 0
          body.week_only = true
        }
      }
      // A new job is made after the visit's body is complete (done, payment, agreement, week: Astra r2, finding 3),
      // so the visit that waits with a job made with no signal is the whole visit.
      if (!clean && pick && newJob) {
        // One new job at a time while the last is still waiting for signal (Astra r1, finding 11).
        const waiting = pendingJobFor(customer.id)
        if (waiting) {
          busy = false; cancel.disabled = false; close.disabled = false; fields.disabled = false; save.disabled = false
          error.textContent = `${waiting.jobName} for ${customer.name} is still waiting for signal. It saves, with its visit, once your phone is back in signal.`
          return
        }
        if (newJobOften.kind === 'regular' && !newJobOften.days) { busy = false; save.disabled = false; fields.disabled = false; cancel.disabled = false; close.disabled = false; error.textContent = 'Pick every how many weeks, or Just once.'; return }
        const name = newJobName.value.trim() || (newJob.kind === 'other' ? 'Another job' : kindLabel(newJob.kind))
        let queuedKey: string | null = null
        const made = await mutate<{ id: number }>({
          label: `Add job: ${name} for ${customer.name}`,
          method: 'POST',
          path: `/api/customers/${customer.id}/jobs`,
          body: { name, kind: newJob.kind, price_pence: pence, cycle_days: newJobOften.kind === 'regular' ? newJobOften.days : null, cycle_text: null },
          onQueued: (m) => { queuedKey = m.key },
        })
        if (!made.ok && queuedKey) {
          // Held in the queue for any reason (no signal, a server error, an expired sign-in: Astra r2, finding 10):
          // the job waits there, and its visit waits with it; nothing else is made.
          keepPendingJob({ jobKey: queuedKey, customerId: customer.id, jobName: name, visit: { ...body, kind: newJob.kind } })
          dialog.close()
          toast(`${name} isn't saved yet. It saves, with its visit, as soon as it can.`)
          return
        }
        if (!made.ok || typeof made.row?.id !== 'number') {
          busy = false; cancel.disabled = false; close.disabled = false; fields.disabled = false; save.disabled = false
          error.textContent = made.error ?? "The new job didn't save."
          return
        }
        body.job_id = made.row.id
        body.kind = newJob.kind
      }
      // The marker comes from what THIS editor loaded (Astra r1, finding 10): marked only when it read the customer's
      // jobs, whatever the round's switch says by the time it saves.
      if (options.customerOffer) {
        body.kind = options.acceptedOffer?.offer.items.some(e => e.kind === 'gutters') ? 'gutter' : 'other'
        // The server makes the offer's own one-off job atomically with this visit.
        delete body.job_id
      }
      const offerLead = options.linkLead && (options.acceptedOffer || bookingQuote) && (!clean || options.addOfferToVisit) && !done ? options.linkLead : null
      const ack = options.customerOffer ? await mutate({label: `Book accepted extras for ${customer.name}`, method: 'POST', path: `/api/customers/${customer.id}/offers`, jobsAware: true, body: {command: 'book', offer_id: options.customerOffer.id, version: options.customerOffer.version, visit: body}}) : offerLead ? await mutate({ label: `Book in: ${customer.name} ${date.value}`, method: 'POST', path: `/api/leads/${offerLead.id}/book`, jobsAware: pick !== null,
        body: { mode: 'existing', version: offerLead.version, customer_id: customer.id, customer_version: customer.version,
          ...(options.acceptedOffer ? { extra_offer: options.acceptedOffer.choice } : {}), first_pence: pence !== null ? pence - (insidePence ?? 0) : null,
          visit: { ...body, ...(options.addOfferToVisit && clean ? { clean_id: clean.id } : {}) } } })
        : await mutate({ label: `${done ? 'Log' : clean ? 'Edit' : 'Book'}: ${customer.name} ${date.value}`, method: clean ? 'PATCH' : 'POST', path, body, jobsAware: pick !== null })
      busy = false; cancel.disabled = false; close.disabled = false
      if (ack.ok) {
        if (options.customerOffer) { dialog.close(); options.onBooked?.(); window.dispatchEvent(new CustomEvent('dropby:data-changed')); toast('Accepted jobs booked for one visit'); return }
        if (offerLead) {
          window.dispatchEvent(new CustomEvent('dropby:data-changed'))
          if (options.addOfferToVisit) {
            const saved = ack.booked?.clean as Clean | undefined
            const receipt = saved ? quoteOfferReceipt(saved) : []
            if (receipt.length) {
              heading.textContent = 'Booked'; clear(content)
              const done = el('button', { type: 'button', class: 'btn-primary' }, 'Done')
              done.addEventListener('click', () => dialog.close())
              content.append(el('section', { class: 'booking-booked-panel', role: 'status' }, el('h3', {}, `${customer.name}'s visit`), ...receipt, done))
            } else { dialog.close(); toast('Accepted extras saved on the visit') }
            return
          }
          renderEnquiryBooked(offerLead, customer, ack); return
        }
        const saved = ack.row as Clean | undefined
        // "Yes, this is them": the visit is booked, so the enquiry is won and goes on their page now (one save).
        if (options.linkLead && !options.acceptedOffer && !done && sameIdentity()) linkEnquiry(options.linkLead, customer)
        if (options.replyConfirmation && !done && saved?.id && saved.date && saved.state === 'booked' && sameIdentity()) {
          // Undo puts back exactly what was there: the visit as it was before (its day, time, agreement), or no visit
          // at all when this save made it (deleted at the version it was saved with).
          const name = customer.name
          const after = (): void => { window.dispatchEvent(new CustomEvent('dropby:data-changed')) }
          undoOnClose = clean?.state === 'booked'
            ? () => undoPatch({ text: `Put back ${name}'s visit as it was`, label: `Undo agreed visit: ${name} ${clean.date}`, path, ack, version: clean.version,
                body: { date: clean.date, time_slot: clean.time_slot ?? null, ...nameSent(clean.worker ?? null), duration_minutes: clean.duration_minutes ?? null, kind: clean.kind,
                  amount_pence: clean.amount_pence ?? null, inside_pence: clean.inside_pence ?? null, extras_json: clean.extras_json ?? null, note: clean.note ?? null, confirmed: clean.confirmed, time_agreed_for: timeIsAgreed(clean) ? clean.time_agreed_for : null,
                  ...(saved.date !== clean.date ? { undo_restore: true } : {}) }, after })
            : !clean && typeof saved.version === 'number'
              ? () => offerUndo(`Booked ${name} for ${dayLabel(saved.date)}`, () => void mutate({ label: `Undo booking: ${name} ${saved.date}`, method: 'DELETE', path: `/api/cleans/${saved.id}`,
                  body: { version: saved.version }, onAck: after, onConflict: after }).then((back) => { if (!back.ok && back.status >= 400 && back.status < 500) window.alert(`Undo was refused: ${back.error ?? 'rejected'}`) }))
              : null
          options.cleanId = saved.id; options.agreedDate = undefined; options.initialVisitNote = undefined
          options.onBooked?.()
          void renderReplyConfirmation(customer, saved)
        } else dialog.close()
        // A booked visit changed here gets the Diary's receipt: Undo puts the visit back as it was, and an agreed visit
        // moved to another day says it needs agreeing again, with "Message them" (bug hunt 8 Oct 2026: it said only
        // "Visit saved"). The accepted-reply flow keeps its own confirmation screen.
        const edited = !done && !options.replyConfirmation && clean?.state === 'booked' && !ack.noop
        // The day it went on lights up once when the Diary is drawn again (the movement set's land; Liam, 24 Oct 2026, 8).
        if (fresh && saved?.id && sameIdentity()) diaryLandOn(String((saved as Partial<Clean>).date ?? body.date))
        if (fresh && saved?.id && typeof saved.version === 'number' && sameIdentity()) bookedReceipt(saved, body)
        else if (!edited) toast(ack.nextBooked ? nextBookedMessage(ack.nextBooked) : done ? 'Completed work recorded' : visitSavedMessage(Boolean(clean)))
        else {
          const savedDate = (ack.row as Partial<Clean> | undefined)?.date ?? String(body.date)
          const moved = savedDate !== clean.date
          const tellThem = moved && clean.confirmed === 1
          undoPatch({
            text: moved ? `Moved ${customer.name} to ${dayLabel(savedDate)}${tellThem ? ' · New date needs agreement' : ''}` : `Saved ${customer.name}'s visit`,
            ...(tellThem ? { link: { label: 'Message them', act: () => void import('./screens/rebook').then((m) => m.openMessagesFor({ id: customer.id, name: customer.name }, 'customer')) } } : {}),
            label: `Undo edit: ${customer.name} ${clean.date}`, path, ack, version: clean.version,
            body: { date: clean.date, time_slot: clean.time_slot ?? null, ...nameSent(clean.worker ?? null), duration_minutes: clean.duration_minutes ?? null, kind: clean.kind,
              amount_pence: clean.amount_pence ?? null, inside_pence: clean.inside_pence ?? null, extras_json: clean.extras_json ?? null, note: clean.note ?? null, confirmed: clean.confirmed, time_agreed_for: timeIsAgreed(clean) ? clean.time_agreed_for : null,
              ...(moved ? { undo_restore: true } : {}) },
            after: () => window.dispatchEvent(new CustomEvent('dropby:data-changed')),
          })
        }
        // The first booking, walked through: the diary opens on its week and shows it (schedule.ts).
        const row = ack.row as { id?: number; date?: string } | undefined
        // Only a visit on a real day: one put in a week has no place on a day card to show (Codex).
        if (options.guide && !done && (!clean || clean.source === 'week') && row?.id && body.week_only !== true) window.dispatchEvent(new CustomEvent('roundly:first-visit', { detail: { id: row.id, date: row.date ?? String(body.date) } }))
        if (done && clean?.state === 'booked' && !ack.noop) options.onCompleted?.()
        window.dispatchEvent(new CustomEvent('dropby:data-changed'))
        if (done && clean && clean.state === 'booked') {
          undoPatch({ text: `Marked ${customer.name} done`, label: `Undo done: ${customer.name} ${date.value}`, path, ack, version: clean.version,
            body: { state: 'booked', confirmed: clean.confirmed, time_agreed_for: timeIsAgreed(clean) ? clean.time_agreed_for : null, date: clean.date, time_slot: clean.time_slot ?? null, amount_pence: clean.amount_pence ?? null, note: clean.note ?? null }, after: () => window.dispatchEvent(new CustomEvent('dropby:data-changed')) })
        }
      } else if (ack.status >= 400 && ack.status < 500) {
        fields.disabled = false; save.disabled = ack.status === 409
        error.textContent = ack.status === 409 ? `${ack.error} Your draft is still here. Close and reopen to review the current visit before retrying.` : ack.error ?? 'Check the fields and try again.'
      } else if (ack.signedOut) {
        error.textContent = 'Your sign-in has expired. This visit is held, not lost: sign in again and it saves itself. You can close this panel.'
        cancel.textContent = 'Close'
      } else {
        error.textContent = 'Waiting for signal. This change is in the retry queue; you can close this panel.'
        cancel.textContent = 'Close'
      }
    })
    focusStart()

    // A new visit's saved bar (canvas artboard Then): what was booked, Undo, and, for a visit not agreed that Messages
    // would ask about now, "Message them about <day>", opening that person's card in Messages (their row, or the extra
    // job's own row) with that day offered. When there is nothing to send, the bar says why. Never the Ask pop-up.
    function bookedReceipt(saved: Clean, sent: Record<string, unknown>): void {
      const today = todayISO()
      const day = saved.date ?? String(sent.date)
      const jobId = typeof sent.job_id === 'number' ? sent.job_id : null
      const week = sent.week_only === true
      const agreedNow = sent.confirmed === 1
      const extra = jobId !== null ? pick?.extra.find((j) => j.id === jobId) : undefined
      const conversation = jobId === null
        ? { ...customer, last_clean: lastDone }
        : { rebook_messages: extra?.rebook_messages ?? 0, rebook_messaged_on: extra?.rebook_messaged_on ?? null, last_clean: extra?.last_clean ?? lastDoneOf({ id: jobId, customer_id: customer.id }, cleans) }
      const otherVisit = cleans.filter((c) => c.id !== saved.id && c.state === 'booked' && c.date >= today && !isContact(c) && visitJobId(c) === jobId).map((c) => c.date).sort()[0] ?? null
      const offer = messageThemOffer({ customer, conversation, jobId, date: day, today, aheadDays: comingUpDays(), agreed: agreedNow, weekOnly: week, way: wayFor(customer), otherVisit })
      const shownTime = shownVisitTime(saved)
      const when = week ? `the week of ${shortDate(mondayOf(day))}` : `${dayLabel(day)}${shownTime ? `, ${shownTime}` : ''}`
      // The agreed half in the shared words (visit-words.ts), so a round with no set times reads "Day agreed" alone.
      const agreedWords = visitWords({ ...saved, state: 'booked', confirmed: 1, time_agreed_for: typeof sent.time_agreed_for === 'string' ? sent.time_agreed_for : null }, customer)
      const words = week ? '' : agreedNow ? ` · ${[agreedWords.day, agreedWords.time === 'time agreed' ? agreedWords.time : ''].filter(Boolean).join(' · ')}` : ' · not agreed yet'
      const after = (): void => { window.dispatchEvent(new CustomEvent('dropby:data-changed')) }
      offerUndo(`Booked ${first || customer.name} for ${when}${words}${!offer.offer && offer.why ? `. ${offer.why}` : ''}`,
        () => void mutate({ label: `Undo booking: ${customer.name} ${day}`, method: 'DELETE', path: `/api/cleans/${saved.id}`, body: { version: saved.version }, onAck: after, onConflict: after })
          .then((back) => { if (!back.ok && back.status >= 400 && back.status < 500) window.alert(`Undo was refused: ${back.error ?? 'rejected'}`) }),
        offer.offer ? { label: `Message them about ${dayLabel(day)}`, act: () => void import('./screens/rebook').then((m) => m.openMessagesFor({ id: customer.id, name: customer.name }, 'customer', { jobId, visitId: saved.id, day })) } : undefined)
      // Drawn as the canvas's Then artboard: the words, then the lemon "Message them about <day>" beside Undo.
      document.querySelector('#undo-bar')?.classList.add('booked-receipt')
    }

    // The new visit, as drawn (canvas artboards New and More open): Which day?, What time?, Agreed with them?, Price,
    // and a fold for who, how long and a note. Every button sets the same boxes the rest of this form reads and saves,
    // so the rules above (the day clears both switches, the time clears Time agreed, the price follows the day and the
    // job, gutters never after the windows, the week with no day) hold exactly as before.
    function layoutNew(): { days: HTMLElement; who: HTMLElement; more: HTMLDetailsElement } {
      const today = todayISO()
      const chip = (label: string, cls = ''): HTMLButtonElement => el('button', { type: 'button', class: `booking-chip${cls ? ` ${cls}` : ''}`, 'aria-pressed': 'false' }, label) as HTMLButtonElement
      const press = (b: HTMLElement, on: boolean): void => { b.setAttribute('aria-pressed', String(on)) }
      const section = (title: string, cls: string): HTMLElement => el('section', { class: `booking-section ${cls}`, 'aria-label': title }, el('h3', { class: 'booking-q' }, title))
      const fire = (node: HTMLElement, ...types: string[]): void => { for (const t of types) node.dispatchEvent(new Event(t)) }
      const moved = (): boolean => presentation === 'edit' && !!clean && !!date.value && date.value !== clean.date
      const paintBook = (): void => {
        // In their parking hours: the button says it goes in anyway (Parking notes, 0084).
        const any = parkingAnyway && presentation !== 'reply'
        save.textContent = presentation === 'reply' ? 'Save visit & review message' : presentation === 'edit' ? (moved() ? bookWords(date.value, '', false, any ? 'Move anyway to' : 'Move to') : any ? 'Save anyway' : 'Save visit') : bookWords(date.value, time.value, weekOnly.checked, any ? 'Book anyway,' : 'Book')
        // With extras, the day's whole price on the button: "Book Mon 26 Oct, 10:00 · £130.00" (design B).
        const added = extras.total()
        if (added > 0 && presentation !== 'reply') {
          let base: number | null = null, ins = 0
          try { base = parseMoney(price.value); ins = insidesOn.checked ? parseMoney(inside.value) ?? 0 : 0 } catch { base = null }
          if (base !== null) save.textContent = `${save.textContent} · ${money(base + ins + added)}`
        }
      }
      repaintBook = paintBook
      // How often, the one question (src/how-often.ts; Liam, 27 Oct 2026, design D): Regular and its weeks, Just once or
      // Ad hoc. The Every list stays behind it as the value sent (an enquiry's own odd number of weeks stays what it is);
      // Just once and Ad hoc send their words, and "Then each time" goes, the one price being both (Astra's check, 6 and 9).
      const howOften = (): HTMLElement => {
        const start = enquiryParts.every.value ? { days: Number(enquiryParts.every.value), marker: null } : enquiryMarker ? { days: null, marker: enquiryMarker } : null
        const picker = howOftenPicker({ value: start, question: 'How often?', onChange: (v, k) => {
          enquiryKind = k
          enquiryMarker = v && !v.days ? v.marker : null
          const days = v?.days ? String(v.days) : ''
          if (days && !Array.from(enquiryParts.every.options).some((o) => o.value === days)) enquiryParts.every.append(el('option', { value: days }, cycleLabel(v!.days, null)))
          enquiryParts.every.value = days
          regularField.hidden = k === 'once' || (k === 'adhoc' && !(enquiry && regularChoiceOn(enquiry)?.choice === 'once'))
          fire(enquiryParts.every, 'change')
        } })
        enquiryKind = picker.kind()
        regularField.hidden = enquiryKind === 'once' || (enquiryKind === 'adhoc' && !(enquiry && regularChoiceOn(enquiry)?.choice === 'once'))
        return picker.node
      }

      // ---- Which day? ----
      const loads = new Map<string, DayLoad>()
      let rows: Clean[] = []
      let roster: RoundDay[] = []
      let known: string[] = []
      let failed = false
      const read = async (from: string, to: string): Promise<void> => {
        try {
          const data = await api<SchedulePayload>(`/api/schedule?from=${from}&to=${to}`)
          if (closed) return
          rows = [...rows.filter((c) => c.date < from || c.date > to), ...data.cleans]
          roster = [...roster.filter((r) => r.date < from || r.date > to), ...(data.round_days ?? [])]
          known = [...new Set([...known, ...(data.workers ?? [])])]
          for (let d = from; d <= to; d = addDaysISO(d, 1)) loads.set(d, dayLoad(rows, d, roster))
          failed = false
        } catch { failed = true }
      }
      const days = section('Which day?', 'booking-which-day')
      const dayButtons = el('div', { class: 'booking-days', role: 'group', 'aria-label': 'Which day?' })
      const lineText = el('span', {})
      const seeDay = el('button', { type: 'button', class: 'link-button booking-see-day', 'aria-expanded': 'false' }, 'See the day')
      const retry = el('button', { type: 'button', class: 'link-button', hidden: 'hidden' }, 'Try again')
      const dayLine = el('p', { class: 'booking-day-line', 'aria-live': 'polite' }, lineText, ' ', seeDay, retry)
      let seeOpen = false
      seeDay.addEventListener('click', () => { seeOpen = !seeOpen; seeDay.setAttribute('aria-expanded', String(seeOpen)); previewBox.hidden = !seeOpen || weekOnly.checked })
      retry.addEventListener('click', () => { failed = false; for (const d of [...loads.keys()]) loads.delete(d); void preview() })
      previewBox.hidden = true
      previewBox.classList.add('booking-day-list')
      const another = chip('Another date…')
      another.removeAttribute('aria-pressed'); another.setAttribute('aria-expanded', 'false')
      const dateBox = field('Date', date)
      dateBox.classList.add('booking-another-date')
      dateBox.hidden = true
      another.addEventListener('click', () => {
        dateBox.hidden = !dateBox.hidden
        another.setAttribute('aria-expanded', String(!dateBox.hidden))
        if (!dateBox.hidden) { date.focus(); try { (date as HTMLInputElement & { showPicker?: () => void }).showPicker?.() } catch { /* the box is there to type in */ } }
      })
      weekOnly.classList.add('booking-chip-check')
      const weekChip = el('label', { class: 'booking-chip booking-week-chip' }, weekOnly, el('span', {}, "Just the week, I'll pick the day later"))
      weekOnlyHint.classList.add('booking-week-hint')
      // Moving an agreed visit: what it does, before it is saved (canvas artboard Edit).
      const moveNote = el('p', { class: 'booking-move-note', role: 'status' })
      const paintMove = (): void => {
        const warn = moved() && clean?.confirmed === 1 && !confirmed.checked
        moveNote.hidden = !warn
        moveNote.textContent = warn ? `Moving it to ${dayLabel(date.value)} turns off Day agreed${timesOn ? ' and Time agreed' : ''}. Messages will then list ${first || customer.name} under "Visit moved · tell them", with the message ready.` : ''
      }
      confirmed.addEventListener('change', paintMove)
      date.addEventListener('change', paintMove)
      // Book them in arranges a day, so it has no "just the week".
      days.append(dayButtons, dayLine, previewBox, moveNote, el('div', { class: 'booking-chips' }, another, ...(fresh && !enquiry ? [weekChip] : [])), dateBox, weekOnlyHint)
      const pickDate = (d: string): void => {
        // The day already chosen, tapped as the day arranged: Day agreed goes on without a change.
        if (d === date.value) { arrangeDay(); return }
        date.value = d
        fire(date, 'change')
      }
      if (dialog.classList.contains('booking-side')) {
        sidePick = pickDate
        date.addEventListener('change', () => markSideDay(date.value))
        markSideDay(date.value)
      }
      // Whether the Diary counts the visit being edited on the day it is saved on (not a suggestion, nor held in a week).
      const countedHere = (): boolean => !!clean && rows.some((c) => c.id === clean.id && dayLoad([c], c.date, []).visits === 1)
      const drawDays = (): void => {
        clear(dayButtons)
        // The visit being edited counts on the day it is saved on, as the Diary shows it (Liam's rule 4: never two places
        // that disagree); once another day is picked that day reads "was here".
        dayButtons.append(...chipDates(today, [date.value, clean?.date, options.agreedDate], undefined, clean?.date ?? null).map((d) => {
          const load = loads.get(d)
          // The weekday and the day's number on their own, the same on every round (a US day label is "Sun, Oct 11").
          const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${d}T12:00:00`).getDay()], n = String(Number(d.slice(8)))
          // The day an edited visit is saved on, once another is picked: "was here".
          const was = moved() && d === clean?.date
          const b = el('button', { type: 'button', class: `booking-day${was ? ' booking-day-was' : ''}`, 'aria-pressed': String(d === date.value), 'aria-label': `${dayLabel(d)}${was ? ', where it is saved now' : load ? `, ${visitsWords(load.visits)}` : ''}`, 'data-date': d },
            el('span', { class: 'booking-day-wd' }, wd ?? ''), el('span', { class: 'booking-day-n' }, n ?? ''), el('span', { class: 'booking-day-load' }, was ? 'was here' : load ? visitsWords(load.visits) : failed ? '' : '…'))
          b.addEventListener('click', () => pickDate(d))
          return b
        }))
        const load = loads.get(date.value)
        // A one-person round's day line names nobody out (Liam, 19 Oct 2026).
        lineText.textContent = !date.value ? '' : load ? dayLineWords(date.value, alone ? { ...load, worker: null } : load, !pricesHidden(), !!clean && date.value === clean.date && countedHere())
          : failed ? `${dayLabel(date.value)}: the diary for this day could not be read, so nothing booked has been checked.` : `${dayLabel(date.value)}: reading the diary…`
        seeDay.hidden = !load
        retry.hidden = !failed
        // The day's visits, as the Diary shows them (See the day), and the overlap check on them.
        previewRows = rows.filter((c) => c.date === date.value && c.id !== clean?.id && c.state !== 'cancelled')
        clear(previewBox)
        // In the Diary's one order for the day (tidy-up, Liam, 20 Oct 2026), Roundly's suggestions after.
        const people = previewRows.filter((c) => !isContact(c))
        const shown = [...diaryDayOrder(people.filter((c) => !isSuggestion(c)), severalJobsOn()), ...people.filter(isSuggestion)]
        for (const c of shown) {
          previewBox.append(el('div', { class: 'preview-job' }, el('span', {}, `${setTimesOn() ? `${shownVisitTime(c) ?? 'no set time'} · ` : ''}${c.customer_name}${visitName(c, load?.worker ? `${load.worker} (that day)` : 'Unassigned')}${pricesHidden() ? '' : ` · ${money(c.amount_pence)}`}`), el('span', { class: 'badge' }, visitWords(c, diaryCustomer(c)).text)))
        }
        if (!shown.length) previewBox.append(el('p', {}, 'No other visits on this day.'))
        else if (timesOn) previewBox.append(el('p', { class: 'muted' }, 'Unknown times and travel are not included in overlap checks.'))
        clear(workers)
        for (const w of known) workers.append(el('option', { value: w }))
        checkOverlap()
        paintWho()
      }
      paintDays = async (): Promise<void> => {
        const sequence = ++previewSequence
        drawDays()
        const last = addDaysISO(today, 5)
        if (!loads.has(today) || !loads.has(last)) await read(today, last)
        for (const d of [date.value, clean?.date, options.agreedDate]) if (d && !loads.has(d) && !failed) await read(d, d)
        if (closed || sequence !== previewSequence) return
        drawDays()
      }

      // ---- What time? ----
      const timeSection = section('What time?', 'booking-what-time')
      const noTime = chip('No set time')
      const setTime = chip('Set a time')
      time.setAttribute('aria-label', 'Time')
      // Roundly's own time wheel (Liam, 27 Oct 2026: the phone's wheel "dissapears like ive clicked away also its not on
      // brand"; "Yes" to it everywhere): shown under the chips once a time is set, the box behind it kept for a keyboard and
      // a screen reader (time-wheel.ts). A time typed by hand before ("9am-11am", a text box) keeps its box.
      const wheeled = time.getAttribute('type') === 'time'
      const timeAt = wheeled ? el('div', { class: 'booking-time-wheel time-chooser-wheeled' }, time) : el('label', { class: 'booking-time-at' }, el('span', {}, 'At'), time)
      // Made once it shows (a wheel out of sight cannot be scrolled to its time).
      let wheel: ReturnType<typeof timeWheel> | null = null
      const makeWheel = (): void => { if (wheeled && !wheel && !timeAt.hidden) { wheel = timeWheel(time as HTMLInputElement); timeAt.append(wheel.el) } }
      if (wheeled) {
        time.classList.add('time-chooser-input', 'time-chooser-one')
        // The wheel settles by `input`; the day's preview and the parking check also listen for `change`.
        time.addEventListener('input', () => { if (wheel) time.dispatchEvent(new Event('change')) })
      }
      let timeOpen = !!time.value.trim()
      const paintTime = (): void => {
        const set = timeOpen || !!time.value.trim()
        press(noTime, !set)
        setTime.hidden = set
        timeAt.hidden = !set
        timeAt.classList.toggle('booking-chip-on', !!time.value.trim())
        makeWheel()
        paintBook()
      }
      noTime.addEventListener('click', () => {
        timeOpen = false
        if (time.value) { time.value = ''; fire(time, 'input', 'change') }
        paintTime()
      })
      setTime.addEventListener('click', () => {
        timeOpen = true
        // Starts on midday, as the Diary's time pop-up does (Liam, 9 Oct 2026), not on the clock's own time.
        if (!time.value) { time.value = '12:00'; fire(time, 'input', 'change') }
        paintTime()
        if (!wheeled) {
          time.focus()
          try { (time as HTMLInputElement & { showPicker?: () => void }).showPicker?.() } catch { /* the box is there to type in */ }
        }
      })
      time.addEventListener('input', paintTime)
      time.addEventListener('change', paintTime)
      // The wheel on its own row under the chips; an old typed time's box stays beside them as before.
      timeSection.append(el('div', { class: 'booking-chips' }, noTime, setTime, ...(wheeled ? [] : [timeAt])), ...(wheeled ? [timeAt] : []), overlap, parkingLine)

      // ---- Agreed with them? ----
      const agreeSection = section('Agreed with them?', 'booking-agreed')
      const switchRow = (input: HTMLInputElement, label: string): HTMLElement => {
        input.setAttribute('role', 'switch')
        input.classList.add('booking-switch-input')
        return el('label', { class: 'booking-switch-row' }, el('span', { class: 'booking-switch-label' }, label), input,
          el('span', { class: 'booking-switch', 'aria-hidden': 'true' }, el('span', { class: 'booking-switch-knob' })))
      }
      agreementHint.textContent = options.arranged
        ? (timesOn ? 'On for the day and time you pick. Turn off anything not agreed yet.' : 'On for the day you pick. Turn it off if it is not agreed yet.')
        : timesOn ? "Turn each on once they've agreed it. Changing the day turns both off; changing the time turns Time agreed off." : "Turn it on once they've agreed the day. Changing the day turns it off."
      agreeSection.append(switchRow(confirmed, 'Day agreed'), ...(timesOn ? [switchRow(timeConfirmed, 'Time agreed')] : []), agreementHint)

      // ---- Price ----
      const priceSection = section('Price', 'booking-price')
      const figure = el('strong', { class: 'booking-price-figure' })
      const usual = chip('')
      const other = chip('Another amount')
      const priceBox = field(`Price ${currencySymbol()}`, price)
      priceBox.classList.add('booking-price-box')
      let priceOpen = false
      // Only the windows (job 1) and an extra job of the customer's own have a price to suggest; gutters or something
      // else picked as the Service, or a new job, are priced by hand.
      const suggested = (): number | null => clean
        ? (kind.value === clean.kind ? outsidePence(clean) ?? null : null)
        : newJob ? null : (job && job.id !== null) || kind.value === 'window' ? suggest().pence ?? null : null
      paintPrice = (): void => {
        let pence: number | null = null
        let bad = false
        try { pence = parseMoney(price.value) } catch { bad = true }
        figure.textContent = bad ? price.value.trim() : pence === null ? 'Not priced yet' : money(pence)
        const s = suggested()
        const why = newJob || (job && job.id !== null) || kind.value !== 'window' ? 'regular' : suggest().why
        usual.hidden = s === null
        other.hidden = s === null
        usual.textContent = s === null ? '' : `${clean ? 'Saved' : enquiry ? 'Quoted' : why === 'adhoc' || why === 'missed' ? 'Ad-hoc' : 'Usual'} ${money(s)}`
        const same = !bad && pence === s
        press(usual, same && !priceOpen)
        press(other, !same || priceOpen)
        priceBox.hidden = s !== null && same && !priceOpen
        paintInsides()
      }
      usual.addEventListener('click', () => {
        const s = suggested()
        if (s === null) return
        priceOpen = false
        price.value = (s / 100).toFixed(2)
        explainPrice()
      })
      other.addEventListener('click', () => { priceOpen = true; paintPrice(); price.focus(); price.select() })
      price.addEventListener('input', paintPrice)
      priceSection.append(serviceField)
      if (quoteChoice) { syncQuoteChoice(); priceSection.append(quoteChoice) }
      // A new job's name and "make it a regular job" sit straight under the Job choice that opened them (new visits only).
      if (pick && !clean) priceSection.append(newJobBox)
      // Insides as well: the switch, the inside price under it, and the two added up.
      const insideBox = field(`Inside price ${currencySymbol()}`, inside)
      insideBox.classList.add('booking-price-box')
      const insideSum = el('p', { class: 'muted field-help booking-inside-sum', 'aria-live': 'polite' })
      paintInsides = (): void => {
        // With the insides on, the first box is plainly the outside price.
        const label = priceBox.querySelector('span')
        if (label) label.textContent = insidesOn.checked ? `Outside price ${currencySymbol()}` : enquiry ? `First clean ${currencySymbol()}` : `Price ${currencySymbol()}`
        insideBox.hidden = !insidesOn.checked
        insideSum.hidden = !insidesOn.checked
        if (!insidesOn.checked) return
        let out: number | null = null, ins: number | null = null
        try { out = parseMoney(price.value); ins = parseMoney(inside.value) } catch { insideSum.textContent = ''; return }
        insideSum.textContent = out === null || ins === null ? 'Outside and inside are saved together as the price.' : `Outside ${money(out)} + Inside ${money(ins)} = ${money(out + ins)}`
      }
      insidesOn.addEventListener('change', () => {
        if (insidesOn.checked && !inside.value.trim() && customer.inside_price_pence != null) inside.value = (customer.inside_price_pence / 100).toFixed(2)
        paintInsides()
        if (insidesOn.checked && !inside.value.trim()) inside.focus()
      })
      inside.addEventListener('input', paintInsides)
      priceSection.append(el('div', { class: 'booking-price-line' }, figure, priceHint), el('div', { class: 'booking-chips' }, usual, other), priceBox, swapTo,
        // Book them in: the insides wait for their page; what they pay after, how often and the postcode come here.
        switchRow(insidesOn, 'Insides too'), insideBox, insideSum,
        // Extras for this visit, under the price (design B), on every new booking and Edit visit.
        extras.el)
      if (enquiry) {
        const after = el('div', { class: 'booking-enquiry-after' },
          howOften(), regularField, field(postcodeWords('Postcode'), enquiryParts.postcode))
        priceSection.append(after, ...(otherJobs?[otherJobs.node]:[]), el('p', { class: 'muted field-help' }, `${quoted ? 'Filled in from the quote: change anything before Book. ' : ''}${customer.parking_restrictions ? `Parking from the quote, kept on their page: ${customer.parking_restrictions} ` : ''}These become ${first || customer.name}'s prices and how often, as a customer. ${postcodeWords('Check the postcode')}: it puts them on the map.`))
      }

      // ---- More: who's doing it, how long, a note ----
      // A one-person round leaves out who's doing it, unless the visit carries someone else's name (askWho).
      const more = el('details', { class: 'booking-more' }) as HTMLDetailsElement
      more.append(el('summary', {}, askWho ? "More: who's doing it, how long, a note" : 'More: how long, a note'))
      const who = el('div', { class: 'booking-more-part', role: 'group', 'aria-label': "Who's doing it?" })
      const whoChips = el('div', { class: 'booking-chips' })
      worker.setAttribute('placeholder', 'Their name')
      const whoBox = field('Name', worker)
      whoBox.classList.add('booking-other-field')
      let whoOther = false
      const paintWho = (): void => {
        // A one-person round's blank is the owner, "You"; its only other name is the one the visit carries.
        const dayWorker = alone ? null : loads.get(date.value)?.worker ?? null
        const v = worker.value.trim().toLowerCase()
        const dayChip = chip(alone ? 'You' : dayWorker ? `${dayWorker}, out that day` : 'Unassigned')
        press(dayChip, !whoOther && (!v || v === dayWorker?.toLowerCase()))
        dayChip.addEventListener('click', () => { whoOther = false; worker.value = ''; fire(worker, 'input', 'change'); paintWho() })
        const named = (alone ? [otherThanOwner(clean?.worker, ownName)].filter(Boolean) : workerChoices(dayWorker, known, worker.value)).map((n) => {
          const b = chip(n)
          press(b, !whoOther && v === n.toLowerCase())
          b.addEventListener('click', () => { whoOther = false; worker.value = n; fire(worker, 'input', 'change'); paintWho() })
          return b
        })
        const someone = chip('Someone else…')
        press(someone, whoOther)
        someone.addEventListener('click', () => { whoOther = true; paintWho(); worker.focus() })
        clear(whoChips)
        whoChips.append(dayChip, ...named, ...(alone ? [] : [someone]))
        whoBox.hidden = !whoOther
      }
      worker.addEventListener('change', () => { if (whoOther && worker.value.trim()) { whoOther = false; paintWho() } })
      who.append(el('p', { class: 'booking-more-q' }, "Who's doing it?"), whoChips, whoBox, workers)
      const long = el('div', { class: 'booking-more-part', role: 'group', 'aria-label': 'How long?' })
      const longChips = el('div', { class: 'booking-chips' })
      duration.setAttribute('placeholder', 'Minutes')
      const longBox = field('Minutes', duration)
      longBox.classList.add('booking-other-field')
      let longOther = false
      const setMinutes = (m: number | null): void => { longOther = false; duration.value = m === null ? '' : String(m); fire(duration, 'input', 'change'); paintLong() }
      const paintLong = (): void => {
        const m = duration.value ? Number(duration.value) : null
        const notSure = chip('Not sure')
        press(notSure, !longOther && m === null)
        notSure.addEventListener('click', () => setMinutes(null))
        const presets = DURATION_PRESETS.map(([mins, label]) => {
          const b = chip(label)
          press(b, !longOther && m === mins)
          b.addEventListener('click', () => setMinutes(mins))
          return b
        })
        // A length saved that is not one of the presets keeps its own button.
        const kept = m !== null && !DURATION_PRESETS.some(([mins]) => mins === m) && !longOther ? [chip(minutesWords(m))] : []
        for (const b of kept) press(b, true)
        const otherLong = chip('Other')
        press(otherLong, longOther)
        otherLong.addEventListener('click', () => { longOther = true; paintLong(); duration.focus() })
        clear(longChips)
        longChips.append(notSure, ...presets, ...kept, otherLong)
        longBox.hidden = !longOther
      }
      duration.addEventListener('change', () => { if (longOther && duration.value) { longOther = false; paintLong() } })
      long.append(el('p', { class: 'booking-more-q' }, 'How long?'), longChips, longBox)
      const noteField = field('Note for this visit (only for you)', note)
      noteField.classList.add('booking-note-field')
      more.append(el('div', { class: 'booking-more-body' }, ...(askWho ? [who] : []), long, noteField))
      more.open = !!((askWho && worker.value.trim()) || duration.value || note.value.trim())

      fields.classList.add('booking-fields-new')
      actions.classList.add('booking-footer-book')
      // No set times: the time question is not on the form at all, not just hidden.
      fields.append(days, ...(timesOn ? [timeSection] : []), agreeSection, priceSection, more)
      afterWeekOnly = (): void => {
        const on = weekOnly.checked
        weekChip.classList.toggle('booking-chip-on', on)
        timeSection.hidden = on || !timesOn
        agreeSection.hidden = on
        dayLine.hidden = on
        previewBox.hidden = on || !seeOpen
        weekOnlyHint.hidden = !on
        paintBook()
      }
      date.addEventListener('change', paintBook)
      paintTime()
      paintLong()
      paintWho()
      paintMove()
      explainPrice()
      applyWeekOnly()
      focusStart = (): void => { dayButtons.querySelector<HTMLElement>('[aria-pressed="true"]')?.focus() }
      return { days, who, more }
    }
  }
  // "Yes, this is them", after their visit is booked: the enquiry won and linked to them (book.ts mode existing).
  function linkEnquiry(lead: Lead, to: Customer): void {
    const who = firstName(lead.name) || lead.name
    void mutate({ label: `Book in: ${lead.name} is ${to.name}`, method: 'POST', path: `/api/leads/${lead.id}/book`, body: { mode: 'existing', version: lead.version, customer_id: to.id, ...(options.offersDeferred ? { defer_offers: true } : {}) } })
      .then((ack) => {
        window.dispatchEvent(new CustomEvent('dropby:data-changed'))
        if (!ack.ok && ack.status >= 400 && ack.status < 500) toast(`The visit is booked, but ${who}'s enquiry is still on its own: ${ack.error ?? 'it did not save'}`)
      })
  }

  // A booking held for signal (Sol, 19 Oct 2026): when it lands, the sheet, if still open, shows it booked with Undo, read
  // back from the round; a booking refused meanwhile says why.
  function watchLanding(lead: Lead, standIn: Customer, key: string, day: string, error: HTMLElement): void {
    const listen = (): void => {
      if (closed) { offQueueChange(listen); return }
      const entry = getQueue().find((q) => q.key === key)
      if (entry && (entry.status === 'queued' || entry.status === 'sending')) return
      offQueueChange(listen)
      if (entry) { error.textContent = entry.error ?? `${firstName(lead.name) || lead.name} wasn't booked in. Check the saving indicator.`; return }
      void (async () => {
        try {
          const row = (await api<{ leads: Lead[] }>('/api/leads')).leads.find((l) => l.id === lead.id)
          const cid = row?.converted_customer_id
          if (!row || typeof cid !== 'number' || closed || !sameIdentity()) return
          const detail = await api<{ customer: Customer; cleans: Clean[] }>(`/api/customers/${cid}`)
          const visit = detail.cleans.find((c) => c.state === 'booked' && c.date === day) ?? null
          if (closed) return
          renderEnquiryBooked(lead, standIn, { ok: true, status: 201, row, booked: { customer: detail.customer as unknown as Record<string, unknown>, clean: visit as unknown as Record<string, unknown> | null } })
        } catch { /* it landed; the card and the Diary show it */ }
      })()
    }
    onQueueChange(listen)
  }

  // Book them in, saved (the drawn "Booked" screen, 19 Oct 2026): a green tick, the day and what is agreed in the shared
  // words, that they are a customer now and off the enquiries, Done, Open in Diary on that day, and Undo, which puts
  // the enquiry back as it was (POST /api/leads/:id/unbook) or says why it can't.
  function renderEnquiryBooked(lead: Lead, standIn: Customer, ack: Awaited<ReturnType<typeof mutate>>): void {
    const who = firstName(lead.name) || lead.name
    const row = ack.row as Lead | undefined
    const made = ack.booked?.customer as Customer | null | undefined
    const visit = ack.booked?.clean as Clean | null | undefined
    heading.textContent = 'Booked'
    dialog.classList.add('booking-booked')
    clear(content)
    const panel = el('section', { class: 'booking-booked-panel', role: 'status' })
    const tick = el('span', { class: 'booking-booked-tick', 'aria-hidden': 'true' }, bookedTick())
    const status = el('p', { class: 'form-error', role: 'alert' })
    const done = el('button', { type: 'button', class: 'btn-primary' }, 'Done')
    done.addEventListener('click', () => dialog.close())
    if (!visit || typeof made?.id !== 'number') {
      // The customer was made but no visit came back (book.ts says so rather than claim a booking).
      const id = row?.converted_customer_id
      panel.append(tick, el('h3', {}, `${who} is a customer now`), el('p', {}, "The visit didn't book. Book it from their page."))
      if (typeof id === 'number') panel.append(el('a', { class: 'btn-secondary', href: `#/customer/${id}` }, 'Open their page'))
      panel.append(done)
      content.append(panel)
      return
    }
    const words = visitWords({ ...visit, state: 'booked' }, { ...standIn, id: made.id } as Customer)
    const shownTime = shownVisitTime(visit)
    const agreed = visit.confirmed === 1 ? [words.day, words.time === 'time agreed' ? words.time : ''].filter(Boolean).join(' · ') : 'not agreed yet'
    const toDiary = el('a', { class: 'btn-secondary', href: `#/schedule?from=${visit.date}` }, 'Open in Diary')
    toDiary.addEventListener('click', () => dialog.close())
    const undo = el('button', { type: 'button', class: 'link-button booking-booked-undo' }, `Undo: put ${who} back as an enquiry`) as HTMLButtonElement
    undo.addEventListener('click', () => {
      if (!row || typeof made.version !== 'number' || typeof visit.version !== 'number') return
      undo.disabled = true
      status.textContent = ''
      void mutate({ label: `Undo booking in: ${lead.name}`, method: 'POST', path: `/api/leads/${lead.id}/unbook`, body: { version: row.version, customer_version: made.version, clean_version: visit.version } })
        .then((back) => {
          window.dispatchEvent(new CustomEvent('dropby:data-changed'))
          if (back.ok) { toast(`${who} is back in your enquiries`); dialog.close(); return }
          if (back.status >= 400 && back.status < 500) { status.textContent = back.error ?? 'Undo was refused.'; return }
          status.textContent = 'Waiting for signal. It undoes as soon as it sends.'
        })
    })
    const welcome=made.other_jobs_welcome?el('button',{type:'button',class:'btn-primary'},`Message ${who} the booking`):null
    if(welcome){done.classList.remove('btn-primary');done.classList.add('btn-secondary');welcome.addEventListener('click',()=>{dialog.close();openBookingWelcome(made,visit)})}
    panel.append(tick, el('h3', {}, `${who} is booked in`),
      el('p', { class: 'booking-booked-when' }, `${dayLabel(visit.date)}${shownTime ? `, ${shownTime}` : ''} · ${agreed}`),
      el('p', { class: 'muted' }, `${who} is a customer now and has gone from your enquiries. The visit is on the Diary.`),
      ...quoteOfferReceipt(visit),
      el('div', { class: 'booking-booked-actions' }, ...(welcome?[welcome]:[]), done, toDiary), undo, status)
    content.append(panel)
    buzz('success')
    done.focus()
  }

  async function renderReplyConfirmation(customer: Customer, saved: Clean): Promise<void> {
    heading.textContent = 'Visit saved · review message'
    clear(content)
    const panel = el('section', { class: 'booking-reply-confirmation' })
    const summary = el('p', { class: 'booking-reply-summary' }, `${dayLabel(saved.date)}${shownVisitTime(saved) ? `, ${shownVisitTime(saved)}` : ''} · ${visitWords(saved, customer).text}`)
    const text = el('textarea', { rows: '7', maxlength: '2000', 'aria-label': 'Confirmation message' })
    text.value = acceptedVisitMessage(customer.name, saved)
    panel.append(el('h3', {}, customer.name), summary)
    if (saved.note) panel.append(el('p', { class: 'booking-reply-note' }, el('strong', {}, 'Visit note · only for you'), el('span', {}, saved.note)))
    panel.append(el('label', { class: 'booking-field' }, el('span', {}, 'Message to send'), text), el('p', { class: 'muted' }, 'Edit the wording before opening your messaging app. Add any details you want to tell them.'))
    const sends = el('div', { class: 'booking-reply-send' })
    const status = el('p', { role: 'status', class: 'muted' })
    const copy = el('button', { type: 'button', class: 'btn-secondary' }, 'Copy message')
    copy.addEventListener('click', async () => {
      if (!sameIdentity()) { status.textContent = 'Your sign-in changed. Close and reopen this visit.'; return }
      if (await copyText(text.value, { button: copy, what: 'message' })) status.textContent = ''
      else { text.focus(); text.select() }
    })
    const edit = el('button', { type: 'button', class: 'btn-secondary' }, 'Edit visit')
    edit.addEventListener('click', () => { if (sameIdentity()) { undoOnClose = null; void loadCustomer(customer.id) } })
    const done = el('button', { type: 'button', class: 'btn-primary' }, 'Done')
    done.addEventListener('click', dismiss)
    panel.append(sends, el('div', { class: 'booking-reply-actions' }, copy, edit, done), status)
    content.append(panel)
    text.focus()
    // The booking may have bumped the customer's version. Read it afresh before
    // mounting the usual messaging menu; never save a preference with a stale row.
    let refreshMessage = (): void => {}
    text.addEventListener('input', () => refreshMessage())
    const drawSend = async (): Promise<void> => {
      if (!sameIdentity()) { sends.textContent = 'Your sign-in changed. Close and reopen this visit.'; return }
      sends.textContent = 'Loading contact details…'
      try {
        const detail = await api<{ customer: Customer }>(`/api/customers/${customer.id}`)
        if (closed || !panel.isConnected || !sameIdentity()) return
        const current = detail.customer
        const control = sendControl(current, text.value, 'Your visit', {
          label: way => way === 'whatsapp' ? 'Open WhatsApp' : wayWords(way).button,
          className: 'btn-whatsapp rebook-send', changed: () => void drawSend(), onConflict: () => void drawSend(),
        })
        const refreshLink = (): void => {
          const next = linkFor(wayFor(current), current, text.value, 'Your visit')
          if (control.link) {
            if (sameIdentity() && text.value.trim() && next.href) control.link.href = next.href
            else control.link.removeAttribute('href')
          }
        }
        for (const event of ['click', 'auxclick', 'contextmenu']) control.root.addEventListener(event, e => { if (!sameIdentity()) { e.preventDefault(); e.stopImmediatePropagation(); status.textContent = 'Your sign-in changed. Close and reopen this visit.' } }, { capture: true })
        refreshMessage = refreshLink
        sends.replaceChildren(control.root)
        refreshLink()
      } catch {
        if (closed || !panel.isConnected) return
        const retry = el('button', { type: 'button', class: 'btn-secondary' }, 'Retry contact details')
        retry.addEventListener('click', () => void drawSend())
        sends.replaceChildren(el('span', {}, 'Visit saved. Contact details could not be loaded.'), retry)
      }
    }
    await drawSend()
  }
  // The short payment panel whenever Today opens it (only Today passes the
  // visit it already has), for a worker or an owner doing their own jobs;
  // the diary's Done still opens the full form (Grok peer notes 2026-09-25).
  if (options.snapshot) {
    renderWorkerDone(options.snapshot)
    return
  }
  if (options.enquiry) {
    const enquiry = options.enquiry
    const open = async (): Promise<void> => {
      try {
        await readBookingQuote(enquiry)
        if (!closed) renderForm(enquiryCustomer(enquiry), [], undefined, false, null)
      } catch (err) { showLoadError(err, open) }
    }
    void open()
  }
  else if (options.customerId) void loadCustomer(options.customerId)
  else void chooseCustomer()
}
